import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE_URL from './config/api';
import CustomSelect from './Components/Shared/CustomSelect';
import SearchableSelect from './Components/Shared/SearchableSelect';
import FileUpload from './Components/Shared/FileUpload';
import UniformCheckInModal from './Components/Shared/UniformCheckInModal';
import { submitStep, getUnitSubmission } from './api/fieldApi';
import { PhotoDedupeContext } from './photoDedupe';

const FAN_COIL_BASE_COMPONENTS = [
  'thermostat',
  'leakSensor',
  'blowerFan',
  'actuator',
  'threeWayValve',
  'drainPan',
  'mould',
  'valves',
  'hoses',
  'coils',
  'insulation',
];

const HEAT_PUMP_EXTRA_COMPONENTS = ['compressor'];

const DRYER_COMPONENTS = [
  'dryerTurningOn',
  'exhaustHoseCondition',
  'boosterFanCondition',
  'bathroomFanCondition',
];

// ERV fields
const ERV_STATUS_FIELDS = ['ervPanelCondition'];
const ERV_DESCRIPTION_FIELDS = ['ervPanelConditionDescription'];
const ERV_ARRAY_FIELDS = ['ervPanelConditionPhoto', 'ervBeforePhoto', 'ervAfterPhoto'];
const ERV_STRING_FIELDS = ['ervType'];

const DRYER_PHOTO_ONLY_FIELDS = [
  { key: 'beforePhotoLintTrap', label: 'Before Photo of Lint Trap' },
  { key: 'afterPhotoLintTrap', label: 'After Photo of Lint Trap' },
  { key: 'beforePhotoBoosterFan', label: 'Before Photo of Booster Fan' },
  { key: 'afterPhotoBoosterFan', label: 'After Photo of Booster Fan' },
  { key: 'beforePhotoBathroomFan', label: 'Before Photo of Bathroom Fan' },
  { key: 'afterPhotoBathroomFan', label: 'After Photo of Bathroom Fan' },
];

const STEP3_STATUS_FIELDS = [
  ...FAN_COIL_BASE_COMPONENTS,
  ...HEAT_PUMP_EXTRA_COMPONENTS,
  ...DRYER_COMPONENTS,
  ...ERV_STATUS_FIELDS,
  'anyOtherCustomIssues',
];

const STEP3_DESCRIPTION_FIELDS = [
  ...FAN_COIL_BASE_COMPONENTS.map((field) => `${field}Description`),
  ...HEAT_PUMP_EXTRA_COMPONENTS.map((field) => `${field}Description`),
  ...DRYER_COMPONENTS.map((field) => `${field}Description`),
  ...ERV_DESCRIPTION_FIELDS,
  'anyOtherCustomIssuesDescription',
];

const STEP3_ARRAY_FIELDS = [
  ...FAN_COIL_BASE_COMPONENTS.map((field) => `${field}Photo`),
  ...HEAT_PUMP_EXTRA_COMPONENTS.map((field) => `${field}Photo`),
  ...DRYER_COMPONENTS.map((field) => `${field}Photo`),
  ...ERV_ARRAY_FIELDS,
  'anyOtherCustomIssuesPhoto',
  ...DRYER_PHOTO_ONLY_FIELDS.map((field) => field.key),
];

const STEP3_EXTRA_STRING_FIELDS = ['boosterFanLocation', ...ERV_STRING_FIELDS];

const ensureStep3FieldDefaults = (data = {}) => {
  const normalized = { ...data };

  STEP3_STATUS_FIELDS.concat(STEP3_DESCRIPTION_FIELDS, STEP3_EXTRA_STRING_FIELDS).forEach((field) => {
    if (normalized[field] === undefined || normalized[field] === null) {
      normalized[field] = '';
    }
  });

  // Coerce ervType to string if it comes back as array
  if (Array.isArray(normalized.ervType)) {
    normalized.ervType = normalized.ervType[0] || '';
  }

  STEP3_ARRAY_FIELDS.forEach((field) => {
    const value = normalized[field];
    normalized[field] = Array.isArray(value) ? value : value ? value : [];
  });

  return normalized;
};

// Schema for Step 1
const step1Schema = z.object({
  unitNumber: z.string().min(1, 'Unit number is required'),
  maintenanceDate: z.string().min(1, 'Maintenance date is required'),
  workerName: z.string().min(1, 'Worker name is required'),
});

// Schema for Step 2 - using superRefine for conditional validation
const step2Schema = z.object({
  equipmentType: z.string().min(1, 'Equipment type is required'),
  equipmentLocation: z.string().min(1, 'Equipment location is required'),
  unitMissed: z.string().min(1, 'Please indicate if the unit was missed'),
  missedUnitPhoto: z.array(z.string()).optional(),
  missedUnitReason: z.string().optional(),
  beforeFanCoilPhoto: z.array(z.string()).optional(),
}).superRefine((data, ctx) => {
  // If unit was missed, require photo and reason
  if (data.unitMissed === 'Yes') {
    if (!data.missedUnitPhoto || data.missedUnitPhoto.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Photo is required when unit is missed',
        path: ['missedUnitPhoto'],
      });
    }
    if (!data.missedUnitReason || data.missedUnitReason.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reason is required when unit is missed',
        path: ['missedUnitReason'],
      });
    }
  }
  if (data.unitMissed === 'No') {
    if (!data.beforeFanCoilPhoto || data.beforeFanCoilPhoto.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Before picture is required',
        path: ['beforeFanCoilPhoto'],
      });
    }
  }
});

// Schema for Step 3 - component checks
// We'll use a dynamic schema based on equipment type
const createStep3Schema = (equipmentType) => {
  if (equipmentType === 'dryer-bathroom-vent') {
    const dryerSchema = {};

    DRYER_COMPONENTS.forEach((component) => {
      dryerSchema[component] = z.string().min(1, `${component.replace(/([A-Z])/g, ' $1')} status is required`);
      dryerSchema[`${component}Photo`] = z.array(z.string()).optional();
      dryerSchema[`${component}Description`] = z.string().optional();
    });

    dryerSchema.boosterFanLocation = z.string().min(1, 'Booster fan location is required');

    DRYER_PHOTO_ONLY_FIELDS.forEach((field) => {
      dryerSchema[field.key] = z.array(z.string()).min(1, `${field.label} is required`);
    });

    dryerSchema.anyOtherCustomIssues = z.string().min(1, 'Any Other Custom Issues status is required');
    dryerSchema.anyOtherCustomIssuesPhoto = z.array(z.string()).optional();
    dryerSchema.anyOtherCustomIssuesDescription = z.string().optional();

    return z.object(dryerSchema).superRefine((data, ctx) => {
      DRYER_COMPONENTS.forEach((component) => {
        const photoKey = `${component}Photo`;
        const descKey = `${component}Description`;

        if (data[component] === 'HAS ISSUES 🔴') {
          if (!data[photoKey] || data[photoKey].length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Photo is required when ${component.replace(/([A-Z])/g, ' $1').trim()} has issues`,
              path: [photoKey],
            });
          }
          if (!data[descKey] || data[descKey].trim() === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Description is required when ${component.replace(/([A-Z])/g, ' $1').trim()} has issues`,
              path: [descKey],
            });
          }
        }
      });

      if (data.anyOtherCustomIssues === 'HAS ISSUES 🔴') {
        if (!data.anyOtherCustomIssuesPhoto || data.anyOtherCustomIssuesPhoto.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Photo is required when custom issues are reported',
            path: ['anyOtherCustomIssuesPhoto'],
          });
        }
        if (!data.anyOtherCustomIssuesDescription || data.anyOtherCustomIssuesDescription.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Description is required when custom issues are reported',
            path: ['anyOtherCustomIssuesDescription'],
          });
        }
      }
    });
  }

  if (equipmentType === 'erv') {
    const ervSchema = {
      ervType: z.string().min(1, 'ERV type is required'),
      ervPanelCondition: z.string().min(1, 'ERV panel condition is required'),
      ervPanelConditionPhoto: z.array(z.string()).optional(),
      ervPanelConditionDescription: z.string().optional(),
      ervBeforePhoto: z.array(z.string()).min(1, 'Before photo is required'),
      ervAfterPhoto: z.array(z.string()).min(1, 'After photo is required'),
      anyOtherCustomIssues: z.string().min(1, 'Any Other Custom Issues status is required'),
      anyOtherCustomIssuesPhoto: z.array(z.string()).optional(),
      anyOtherCustomIssuesDescription: z.string().optional(),
    };

    return z.object(ervSchema).superRefine((data, ctx) => {
      if (data.ervPanelCondition === 'HAS ISSUES 🔴') {
        if (!data.ervPanelConditionPhoto || data.ervPanelConditionPhoto.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Photo is required when ERV panel has issues',
            path: ['ervPanelConditionPhoto'],
          });
        }
        if (!data.ervPanelConditionDescription || data.ervPanelConditionDescription.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Description is required when ERV panel has issues',
            path: ['ervPanelConditionDescription'],
          });
        }
      }

      if (data.anyOtherCustomIssues === 'HAS ISSUES 🔴') {
        if (!data.anyOtherCustomIssuesPhoto || data.anyOtherCustomIssuesPhoto.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Photo is required when custom issues are reported',
            path: ['anyOtherCustomIssuesPhoto'],
          });
        }
        if (!data.anyOtherCustomIssuesDescription || data.anyOtherCustomIssuesDescription.trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Description is required when custom issues are reported',
            path: ['anyOtherCustomIssuesDescription'],
          });
        }
      }
    });
  }

  const components = [...FAN_COIL_BASE_COMPONENTS];
  if (equipmentType === 'heat-pump') {
    components.push(...HEAT_PUMP_EXTRA_COMPONENTS);
  }

  const baseSchema = {};

  components.forEach((component) => {
    const label = component.replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase());
    baseSchema[component] = z.string().min(1, `${label} status is required`);
    baseSchema[`${component}Photo`] = z.array(z.string()).optional();
    baseSchema[`${component}Description`] = z.string().optional();
  });

  baseSchema.anyOtherCustomIssues = z.string().min(1, 'Any Other Custom Issues status is required');
  baseSchema.anyOtherCustomIssuesPhoto = z.array(z.string()).optional();
  baseSchema.anyOtherCustomIssuesDescription = z.string().optional();

  return z.object(baseSchema).superRefine((data, ctx) => {
    components.forEach((component) => {
      const photoKey = `${component}Photo`;
      const descKey = `${component}Description`;

      if (data[component] === 'HAS ISSUES 🔴') {
        if (!data[photoKey] || data[photoKey].length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Photo is required when ${component.replace(/([A-Z])/g, ' $1').trim()} has issues`,
            path: [photoKey],
          });
        }
        if (!data[descKey] || data[descKey].trim() === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Description is required when ${component.replace(/([A-Z])/g, ' $1').trim()} has issues`,
            path: [descKey],
          });
        }
      }
    });

    if (data.anyOtherCustomIssues === 'HAS ISSUES 🔴') {
      if (!data.anyOtherCustomIssuesPhoto || data.anyOtherCustomIssuesPhoto.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Photo is required when custom issues are reported',
          path: ['anyOtherCustomIssuesPhoto'],
        });
      }
      if (!data.anyOtherCustomIssuesDescription || data.anyOtherCustomIssuesDescription.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Description is required when custom issues are reported',
          path: ['anyOtherCustomIssuesDescription'],
        });
      }
    }
  });
};

// Filter tracking only applies to the equipment that actually takes a filter.
const FILTER_EQUIPMENT_TYPES = ['fan-coil', 'heat-pump'];

const FILTER_SIZE_OPTIONS = [
  '14 x 20',
  '16 x 20',
  '18 x 20',
  '20 x 20',
  '14 x 24',
  '16 x 24',
  '18 x 24',
  '20 x 24',
  'Other',
].map((size) => ({ id: size, name: size, value: size }));

// Schema for Step 4
const createStep4Schema = (equipmentType) => {
  const baseSchema = {
    modelStickerPlatePhoto: z.array(z.string()).min(1, 'Photo of model sticker plate is required'),
    additionalPhotos: z.array(z.string()).optional(),
    residentEmail: z.string().email('Please enter a valid email address').optional().or(z.literal('')),
    additionalNotes: z.string().optional(),
    filterSize: z.string().optional(),
    filterSizeOther: z.string().optional(),
    newFilterPhoto: z.array(z.string()).optional(),
  };

  if (!FILTER_EQUIPMENT_TYPES.includes(equipmentType)) {
    return z.object(baseSchema);
  }

  return z
    .object({
      ...baseSchema,
      filterSize: z.string().min(1, 'Filter size is required'),
      newFilterPhoto: z.array(z.string()).min(1, 'Photo of the new filter is required'),
    })
    .superRefine((data, ctx) => {
      if (data.filterSize === 'Other' && !(data.filterSizeOther || '').trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please describe the filter size',
          path: ['filterSizeOther'],
        });
      }
    });
};

export default function AddUnitForm() {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const maintenanceID = searchParams.get('maintenanceID') || '';
  const buildingName = searchParams.get('buildingName') || '';
  const unitIdFromUrl = searchParams.get('unitId') || null; // unitId from URL (for editing)

  const [currentStep, setCurrentStep] = useState(1);
  const [workers, setWorkers] = useState([]);
  const [isLoadingWorkers, setIsLoadingWorkers] = useState(true);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(false);
  const [isSubmittingFinal, setIsSubmittingFinal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({ hasIssues: false, loginId: null });
  
  // Store original values when loading existing data (for change detection)
  const [originalValues, setOriginalValues] = useState(null);

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);
  
  // Prevent body scrolling when success modal is open
  useEffect(() => {
    if (showSuccessModal) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scrolling when modal closes
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showSuccessModal]);
  
  // Track upload status from each FileUpload component
  const [uploadStatuses, setUploadStatuses] = useState({});
  
  // Check if any files are currently uploading
  const isAnyFileUploading = Object.values(uploadStatuses).some(status => status === true);

  // ─── Daily Uniform & Name Tag check-in gate ────────────────────────────
  // Before saving the very first inspection of the day for a given
  // technician, force a live selfie + name tag check. We remember the
  // result in localStorage so it doesn't re-prompt on subsequent units the
  // same day, and we also confirm against the backend so the prompt is
  // honoured across devices/incognito sessions.
  //
  // Enabled fleet-wide: every building requires the daily uniform check
  // on the first submission of the day. The pilot at 123 Hudson St
  // validated the flow end-to-end; we now roll it out to all sites.
  const requiresUniformCheck = true;

  // A "service day" rolls over at 6 AM local time so that late-night
  // edits or backfilled units (e.g. a missed unit added at 12:11 AM)
  // count toward the day the tech was actually on site — they don't
  // satisfy the next morning's uniform check. We key the gate on the
  // service day of *NOW*, not the maintenanceDate the tech selected in
  // the form: the uniform photo is verifying that they're in uniform
  // *right now*, regardless of what date they're back-filling.
  const SERVICE_DAY_ROLLOVER_HOUR = 6;
  const serviceDayFromDate = (d) => {
    const local = new Date(d.getTime());
    if (local.getHours() < SERVICE_DAY_ROLLOVER_HOUR) {
      local.setDate(local.getDate() - 1);
    }
    const yyyy = local.getFullYear();
    const mm = String(local.getMonth() + 1).padStart(2, '0');
    const dd = String(local.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const serviceDayNow = () => serviceDayFromDate(new Date());

  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [checkInDone, setCheckInDone] = useState(false);
  const [checkInLookupInFlight, setCheckInLookupInFlight] = useState(false);
  const [pendingStep1Submit, setPendingStep1Submit] = useState(null);
  // Build the per-day key the same way for read + write so the cache hits
  // even if the user reloads mid-day.
  const checkInLocalKey = (worker, dateStr) => `hudson_uniform_check_in_${worker}_${dateStr}`;
  const componentIssuePlaceholders = {
    blowerFan: 'Describe the blower fan issue in detail (ex. Not turning on, Weak, Grinding, Noisy, Etc.)',
    actuator: 'Describe the actuator issue in detail (ex. Stuck open, Stuck closed, Not opening/closing all the way, Etc.)',
    drainPan: 'Describe the drain pan issue in detail (ex. Rust, Leaking, Etc.)',
    mould: 'Describe what you see and where (e.g. Severity: Low/Medium/High; Location: wires, cabinet, baffle). Note visual signs only — do not claim lab-confirmed mould.',
    insulation: 'Describe the insulation issue in detail (ex. Level of deterioration: Low, Medium, High)',
    compressor: 'Describe the compressor issue in detail (ex. Noisy, Not turning on, Etc.)',
  };
  const renderComponentCheck = (componentKey, componentLabel) => {
    const statusValue = step3Form.watch(componentKey);
    const hasIssues = statusValue === 'HAS ISSUES 🔴';

    return (
      <div key={componentKey} style={{ marginBottom: '2.5rem' }}>
        <CustomSelect
          label={componentLabel}
          required
          placeholder="Select status"
          value={statusValue}
          onChange={(value) => {
            step3Form.setValue(componentKey, value, { shouldValidate: true });

            if (value === 'OK 🟢') {
              step3Form.setValue(`${componentKey}Photo`, [], { shouldValidate: true });
              step3Form.setValue(`${componentKey}Description`, '', { shouldValidate: true });
            }
          }}
          options={[
            { id: 'OK 🟢', name: 'OK 🟢' },
            { id: 'HAS ISSUES 🔴', name: 'HAS ISSUES 🔴' },
          ]}
          hasError={!!step3Form.formState.errors[componentKey]}
          errorMessage={step3Form.formState.errors[componentKey]?.message}
        />

        {hasIssues && (
          <div style={{ marginLeft: '1.5rem', paddingLeft: '1.5rem', borderLeft: '2px solid #e5e7eb' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <FileUpload
                label={`Photo of ${componentLabel} Issue`}
                required
                value={step3Form.watch(`${componentKey}Photo`) || []}
                onChange={(urls) => {
                  step3Form.setValue(`${componentKey}Photo`, urls, { shouldValidate: true });
                }}
                onUploadStatusChange={(isUploading) => {
                  setUploadStatuses(prev => ({ ...prev, [`${componentKey}Photo`]: isUploading }));
                }}
                hasError={!!step3Form.formState.errors[`${componentKey}Photo`]}
                errorMessage={step3Form.formState.errors[`${componentKey}Photo`]?.message}
                multiple
                dedupeField={`${componentLabel} issue`}
              />
            </div>

            <div style={{ marginBottom: '2.5rem' }}>
              <label
                htmlFor={`${componentKey}Description`}
                style={{
                  display: 'block',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.75rem',
                }}
              >
                Description of {componentLabel} Issue (Be Specific) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                id={`${componentKey}Description`}
                {...step3Form.register(`${componentKey}Description`)}
                placeholder={
                  componentIssuePlaceholders[componentKey]
                  || `Describe the ${componentLabel.toLowerCase()} issue in detail...`
                }
                rows={4}
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1rem',
                  border: step3Form.formState.errors[`${componentKey}Description`]
                    ? '1px solid #ef4444'
                    : '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = step3Form.formState.errors[`${componentKey}Description`]
                    ? '#ef4444'
                    : '#d1d5db';
                }}
              />
              {step3Form.formState.errors[`${componentKey}Description`] && (
                <p
                  style={{
                    marginTop: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#ef4444',
                  }}
                >
                  {step3Form.formState.errors[`${componentKey}Description`]?.message}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCustomIssuesBlock = () => {
    const customIssuesValue = step3Form.watch('anyOtherCustomIssues');
    const hasCustomIssues = customIssuesValue === 'HAS ISSUES 🔴';

    return (
      <div style={{ marginBottom: '2.5rem' }}>
        <CustomSelect
          label="Any Other Custom Issues?"
          required
          placeholder="Select status"
          value={customIssuesValue}
          onChange={(value) => {
            step3Form.setValue('anyOtherCustomIssues', value, { shouldValidate: true });

            if (value === 'OK 🟢') {
              step3Form.setValue('anyOtherCustomIssuesPhoto', [], { shouldValidate: true });
              step3Form.setValue('anyOtherCustomIssuesDescription', '', { shouldValidate: true });
            }
          }}
          options={[
            // Keep the persisted values unchanged so every downstream issue
            // detector and resident/manager view remains backward-compatible.
            { id: 'custom-yes', name: 'Yes 🔴', value: 'HAS ISSUES 🔴' },
            { id: 'custom-no', name: 'No 🟢', value: 'OK 🟢' },
          ]}
          hasError={!!step3Form.formState.errors.anyOtherCustomIssues}
          errorMessage={step3Form.formState.errors.anyOtherCustomIssues?.message}
        />

        {hasCustomIssues && (
          <div style={{ marginLeft: '1.5rem', paddingLeft: '1.5rem', borderLeft: '2px solid #e5e7eb' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <FileUpload
                label="Photo of Custom Issue"
                required
                value={step3Form.watch('anyOtherCustomIssuesPhoto') || []}
                onChange={(urls) => {
                  step3Form.setValue('anyOtherCustomIssuesPhoto', urls, { shouldValidate: true });
                }}
                onUploadStatusChange={(isUploading) => {
                  setUploadStatuses(prev => ({ ...prev, anyOtherCustomIssuesPhoto: isUploading }));
                }}
                hasError={!!step3Form.formState.errors.anyOtherCustomIssuesPhoto}
                errorMessage={step3Form.formState.errors.anyOtherCustomIssuesPhoto?.message}
                multiple
                dedupeField="Custom issue"
              />
            </div>

            <div style={{ marginBottom: '2.5rem' }}>
              <label
                htmlFor="anyOtherCustomIssuesDescription"
                style={{
                  display: 'block',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.75rem',
                }}
              >
                Description of Custom Issue (Be Specific) <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                id="anyOtherCustomIssuesDescription"
                {...step3Form.register('anyOtherCustomIssuesDescription')}
                placeholder="Describe the custom issue in detail..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1rem',
                  border: step3Form.formState.errors.anyOtherCustomIssuesDescription
                    ? '1px solid #ef4444'
                    : '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = step3Form.formState.errors.anyOtherCustomIssuesDescription
                    ? '#ef4444'
                    : '#d1d5db';
                }}
              />
              {step3Form.formState.errors.anyOtherCustomIssuesDescription && (
                <p
                  style={{
                    marginTop: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#ef4444',
                  }}
                >
                  {step3Form.formState.errors.anyOtherCustomIssuesDescription?.message}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Track unitId for this submission (for editing/updating)
  const [unitId, setUnitId] = useState(unitIdFromUrl);
  
  // Store all form data as we progress through steps
  const [formData, setFormData] = useState({
    step1: {
      unitNumber: '',
      maintenanceDate: '',
      workerName: '',
    },
    step2: {
      equipmentType: '',
      equipmentLocation: '',
      unitMissed: 'No', // Default to "No"
      missedUnitPhoto: [],
      missedUnitReason: '',
      beforeFanCoilPhoto: [],
    },
    step3: ensureStep3FieldDefaults(),
    step4: {
      modelStickerPlatePhoto: [],
      additionalPhotos: [],
      residentEmail: '',
      additionalNotes: '',
      filterSize: '',
      filterSizeOther: '',
      newFilterPhoto: [],
    },
  });

  // Get today's date in YYYY-MM-DD format for the date input
  const today = new Date().toISOString().split('T')[0];

  // Form for Step 1
  const step1Form = useForm({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      unitNumber: formData.step1.unitNumber || '',
      maintenanceDate: formData.step1.maintenanceDate || today,
      workerName: formData.step1.workerName || '',
    },
  });

  // Form for Step 2
  const step2Form = useForm({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      equipmentType: formData.step2.equipmentType || '',
      equipmentLocation: formData.step2.equipmentLocation || '',
      unitMissed: formData.step2.unitMissed || 'No',
      missedUnitPhoto: formData.step2.missedUnitPhoto || [],
      missedUnitReason: formData.step2.missedUnitReason || '',
      beforeFanCoilPhoto: formData.step2.beforeFanCoilPhoto || [],
    },
  });

  // Get equipment type for Step 3 schema - use formData from Step 2 or current form value
  const equipmentType = formData.step2.equipmentType || step2Form.watch('equipmentType');
  
  // Normalize equipment type for Step 3 rendering
  // Handle both "Fan Coil" (new format) and "fan-coil" (old format) for backwards compatibility
  const normalizedEquipmentType =
    equipmentType === 'Fan Coil' || equipmentType === 'fan-coil' ? 'fan-coil' :
    equipmentType === 'Heat Pump' || equipmentType === 'heat-pump' ? 'heat-pump' :
    equipmentType === 'Dryer / Bathroom Vent' || equipmentType === 'dryer-bathroom-vent' ? 'dryer-bathroom-vent' :
    equipmentType === 'ERV' || equipmentType === 'erv' ? 'erv' :
    equipmentType;
  
  const step3Resolver = normalizedEquipmentType
    ? zodResolver(createStep3Schema(normalizedEquipmentType))
    : undefined;

  // Form for Step 3 - dynamic schema based on equipment type
  // Only initialize if we have an equipment type
  const step3Form = useForm({
    resolver: step3Resolver,
    defaultValues: ensureStep3FieldDefaults(formData.step3),
  });

  // Filter details are only collected for equipment that takes a filter.
  const tracksFilterDetails = FILTER_EQUIPMENT_TYPES.includes(normalizedEquipmentType);
  const filterEquipmentLabel = normalizedEquipmentType === 'heat-pump' ? 'heat pump' : 'fan coil';

  // Form for Step 4 - filter fields are required only for fan coil / heat pump
  const step4Form = useForm({
    resolver: zodResolver(createStep4Schema(normalizedEquipmentType)),
    defaultValues: {
      modelStickerPlatePhoto: formData.step4.modelStickerPlatePhoto || [],
      additionalPhotos: formData.step4.additionalPhotos || [],
      residentEmail: formData.step4.residentEmail || '',
      additionalNotes: formData.step4.additionalNotes || '',
      filterSize: formData.step4.filterSize || '',
      filterSizeOther: formData.step4.filterSizeOther || '',
      newFilterPhoto: formData.step4.newFilterPhoto || [],
    },
  });

  // Load existing unit data if unitId is provided (for editing/resuming)
  useEffect(() => {
    async function loadExistingUnitData() {
      if (!unitIdFromUrl) {
        return; // No unitId, this is a new submission
      }

      setIsLoadingExistingData(true);
      try {
        console.log('[AddUnitForm] Loading existing unit data for unitId:', unitIdFromUrl);
        const existingData = await getUnitSubmission(unitIdFromUrl);
        console.log('[AddUnitForm] Loaded existing data:', existingData);
        console.log('[AddUnitForm] equipment_missed check:', {
          step1_unitMissed: existingData.step1?.unitMissed,
          step2_unitMissed: existingData.step2?.unitMissed,
          raw_equipment_missed: existingData.equipment_missed,
        });

        // Populate formData with existing values
        setFormData({
          step1: {
            unitNumber: existingData.step1?.unitNumber || '',
            maintenanceDate: existingData.step1?.maintenanceDate || today,
            workerName: existingData.step1?.workerName || '',
          },
          step2: {
            equipmentType: existingData.step2?.equipmentType || '',
            equipmentLocation: existingData.step2?.equipmentLocation || '',
            // Check if equipment_missed has a value - if it does, unit was missed ("Yes"), otherwise "No"
            // The backend returns unitMissed as the result of from_array(equipment_missed):
            // - If equipment_missed = [] → unitMissed = "" (empty string)
            // - If equipment_missed = ["Fan Coil"] → unitMissed = "Fan Coil" (equipment type)
            unitMissed: (() => {
              const unitMissedValue = existingData.step2?.unitMissed || existingData.step1?.unitMissed;
              
              // If unitMissed is already "Yes" or "No", use it directly
              if (unitMissedValue === 'Yes' || unitMissedValue === 'No') {
                return unitMissedValue;
              }
              
              // If unitMissed has any other value (like "Fan Coil"), it means unit was missed
              if (unitMissedValue && unitMissedValue.trim() !== '') {
                return 'Yes';
              }
              
              // If unitMissed is empty string or undefined/null, unit was not missed
              return 'No';
            })(),
            missedUnitPhoto: existingData.step2?.missedUnitPhoto || existingData.step1?.missedUnitPhoto || [],
            missedUnitReason: existingData.step2?.missedUnitReason || existingData.step1?.missedUnitReason || '',
            beforeFanCoilPhoto: existingData.step2?.beforeFanCoilPhoto || [],
          },
          step3: ensureStep3FieldDefaults(existingData.step3 || {}),
          step4: {
            modelStickerPlatePhoto: existingData.step4?.modelStickerPlatePhoto || [],
            additionalPhotos: existingData.step4?.additionalPhotos || [],
            residentEmail: existingData.step4?.residentEmail || '',
            additionalNotes: existingData.step4?.additionalNotes || '',
            filterSize: existingData.step4?.filterSize || '',
            filterSizeOther: existingData.step4?.filterSizeOther || '',
            newFilterPhoto: existingData.step4?.newFilterPhoto || [],
          },
        });

        // Set unitId state
        setUnitId(existingData.unitId || unitIdFromUrl);

        // Store original values for change detection
        setOriginalValues({
          step1: {
            unitNumber: existingData.step1?.unitNumber || '',
            maintenanceDate: existingData.step1?.maintenanceDate || today,
            workerName: existingData.step1?.workerName || '',
          },
          step2: {
            equipmentType: existingData.step2?.equipmentType || '',
            equipmentLocation: existingData.step2?.equipmentLocation || '',
            unitMissed: (() => {
              const unitMissedValue = existingData.step2?.unitMissed || existingData.step1?.unitMissed;
              if (unitMissedValue === 'Yes' || unitMissedValue === 'No') {
                return unitMissedValue;
              }
              if (unitMissedValue && unitMissedValue.trim() !== '') {
                return 'Yes';
              }
              return 'No';
            })(),
            missedUnitPhoto: existingData.step2?.missedUnitPhoto || [],
            missedUnitReason: existingData.step2?.missedUnitReason || '',
            beforeFanCoilPhoto: existingData.step2?.beforeFanCoilPhoto || [],
          },
          step3: ensureStep3FieldDefaults({
            // Store all Step 3 component values
            thermostat: existingData.step3?.thermostat || '',
            leakSensor: existingData.step3?.leakSensor || '',
            blowerFan: existingData.step3?.blowerFan || '',
            actuator: existingData.step3?.actuator || '',
            threeWayValve: existingData.step3?.threeWayValve || '',
            drainPan: existingData.step3?.drainPan || '',
            mould: existingData.step3?.mould || '',
            valves: existingData.step3?.valves || '',
            hoses: existingData.step3?.hoses || '',
            coils: existingData.step3?.coils || '',
            insulation: existingData.step3?.insulation || '',
            compressor: existingData.step3?.compressor || '',
            // Dryer components
            dryerTurningOn: existingData.step3?.dryerTurningOn || '',
            exhaustHoseCondition: existingData.step3?.exhaustHoseCondition || '',
            boosterFanCondition: existingData.step3?.boosterFanCondition || '',
            bathroomFanCondition: existingData.step3?.bathroomFanCondition || '',
            anyOtherCustomIssues: existingData.step3?.anyOtherCustomIssues || '',
            // Photos
            thermostatPhoto: existingData.step3?.thermostatPhoto || [],
            leakSensorPhoto: existingData.step3?.leakSensorPhoto || [],
            blowerFanPhoto: existingData.step3?.blowerFanPhoto || [],
            actuatorPhoto: existingData.step3?.actuatorPhoto || [],
            threeWayValvePhoto: existingData.step3?.threeWayValvePhoto || [],
            drainPanPhoto: existingData.step3?.drainPanPhoto || [],
            mouldPhoto: existingData.step3?.mouldPhoto || [],
            valvesPhoto: existingData.step3?.valvesPhoto || [],
            hosesPhoto: existingData.step3?.hosesPhoto || [],
            coilsPhoto: existingData.step3?.coilsPhoto || [],
            insulationPhoto: existingData.step3?.insulationPhoto || [],
            compressorPhoto: existingData.step3?.compressorPhoto || [],
            // Dryer photos
            dryerTurningOnPhoto: existingData.step3?.dryerTurningOnPhoto || [],
            exhaustHoseConditionPhoto: existingData.step3?.exhaustHoseConditionPhoto || [],
            boosterFanConditionPhoto: existingData.step3?.boosterFanConditionPhoto || [],
            bathroomFanConditionPhoto: existingData.step3?.bathroomFanConditionPhoto || [],
            anyOtherCustomIssuesPhoto: existingData.step3?.anyOtherCustomIssuesPhoto || [],
            // Dryer photo-only fields
            beforePhotoLintTrap: existingData.step3?.beforePhotoLintTrap || [],
            afterPhotoLintTrap: existingData.step3?.afterPhotoLintTrap || [],
            beforePhotoBoosterFan: existingData.step3?.beforePhotoBoosterFan || [],
            afterPhotoBoosterFan: existingData.step3?.afterPhotoBoosterFan || [],
            beforePhotoBathroomFan: existingData.step3?.beforePhotoBathroomFan || [],
            afterPhotoBathroomFan: existingData.step3?.afterPhotoBathroomFan || [],
            // Descriptions
            thermostatDescription: existingData.step3?.thermostatDescription || '',
            leakSensorDescription: existingData.step3?.leakSensorDescription || '',
            blowerFanDescription: existingData.step3?.blowerFanDescription || '',
            actuatorDescription: existingData.step3?.actuatorDescription || '',
            threeWayValveDescription: existingData.step3?.threeWayValveDescription || '',
            drainPanDescription: existingData.step3?.drainPanDescription || '',
            mouldDescription: existingData.step3?.mouldDescription || '',
            valvesDescription: existingData.step3?.valvesDescription || '',
            hosesDescription: existingData.step3?.hosesDescription || '',
            coilsDescription: existingData.step3?.coilsDescription || '',
            insulationDescription: existingData.step3?.insulationDescription || '',
            compressorDescription: existingData.step3?.compressorDescription || '',
            // Dryer descriptions
            dryerTurningOnDescription: existingData.step3?.dryerTurningOnDescription || '',
            exhaustHoseConditionDescription: existingData.step3?.exhaustHoseConditionDescription || '',
            boosterFanConditionDescription: existingData.step3?.boosterFanConditionDescription || '',
            bathroomFanConditionDescription: existingData.step3?.bathroomFanConditionDescription || '',
            anyOtherCustomIssuesDescription: existingData.step3?.anyOtherCustomIssuesDescription || '',
            // Extra fields
            boosterFanLocation: existingData.step3?.boosterFanLocation || '',
          }),
          step4: {
            modelStickerPlatePhoto: existingData.step4?.modelStickerPlatePhoto || [],
            additionalPhotos: existingData.step4?.additionalPhotos || [],
            residentEmail: existingData.step4?.residentEmail || '',
            additionalNotes: existingData.step4?.additionalNotes || '',
            filterSize: existingData.step4?.filterSize || '',
            filterSizeOther: existingData.step4?.filterSizeOther || '',
            newFilterPhoto: existingData.step4?.newFilterPhoto || [],
          },
        });

        // Always start at Step 1 when editing (with data prefilled)
        console.log('[AddUnitForm] Starting at Step 1 (edit mode)');
        setCurrentStep(1);

        // Note: Form fields will be populated in a separate useEffect after forms are initialized

      } catch (error) {
        console.error('[AddUnitForm] Error loading existing unit data:', error);
        alert(`Failed to load unit data: ${error.message}`);
      } finally {
        setIsLoadingExistingData(false);
      }
    }

    loadExistingUnitData();
  }, [unitIdFromUrl]); // Only run when unitIdFromUrl changes

  // Populate form fields when formData is loaded (for editing)
  // This runs after forms are initialized and formData is populated
  useEffect(() => {
    if (!unitIdFromUrl || isLoadingExistingData) {
      return; // Don't populate if not editing or still loading
    }

    // Only populate if we have data (not empty defaults)
    const hasStep1Data = formData.step1.unitNumber && formData.step1.unitNumber !== '';
    const hasStep2Data = formData.step2.equipmentType && formData.step2.equipmentType !== '';
    const hasStep3Data = (() => {
      const step3 = formData.step3 || {};
      const hvacFields = [...FAN_COIL_BASE_COMPONENTS, ...HEAT_PUMP_EXTRA_COMPONENTS];
      const dryerFields = [...DRYER_COMPONENTS];
      return (
        hvacFields.some((field) => step3[field] && step3[field] !== '') ||
        dryerFields.some((field) => step3[field] && step3[field] !== '') ||
        (step3.anyOtherCustomIssues && step3.anyOtherCustomIssues !== '')
      );
    })();
    const hasStep4Data = (formData.step4.modelStickerPlatePhoto && formData.step4.modelStickerPlatePhoto.length > 0)
      || Boolean(formData.step4.filterSize)
      || (formData.step4.newFilterPhoto && formData.step4.newFilterPhoto.length > 0);

    // Populate Step 1 form
    if (hasStep1Data) {
      step1Form.reset({
        unitNumber: formData.step1.unitNumber,
        maintenanceDate: formData.step1.maintenanceDate || today,
        workerName: formData.step1.workerName,
        unitMissed: formData.step1.unitMissed || 'No',
        missedUnitPhoto: formData.step1.missedUnitPhoto || [],
        missedUnitReason: formData.step1.missedUnitReason || '',
      });
    }

    // Populate Step 2 form
    if (hasStep2Data) {
      step2Form.reset({
        equipmentType: formData.step2.equipmentType,
        equipmentLocation: formData.step2.equipmentLocation,
        unitMissed: formData.step2.unitMissed || 'No',
        missedUnitPhoto: formData.step2.missedUnitPhoto || [],
        missedUnitReason: formData.step2.missedUnitReason || '',
        beforeFanCoilPhoto: formData.step2.beforeFanCoilPhoto || [],
      });
    }

    // Populate Step 3 form
    if (hasStep3Data) {
      step3Form.reset(ensureStep3FieldDefaults(formData.step3));
    }

    // Populate Step 4 form
    if (hasStep4Data) {
      step4Form.reset({
        modelStickerPlatePhoto: formData.step4.modelStickerPlatePhoto,
        additionalPhotos: formData.step4.additionalPhotos || [],
        residentEmail: formData.step4.residentEmail || '',
        additionalNotes: formData.step4.additionalNotes || '',
        filterSize: formData.step4.filterSize || '',
        filterSizeOther: formData.step4.filterSizeOther || '',
        newFilterPhoto: formData.step4.newFilterPhoto || [],
      });
    }
  }, [formData.step1.unitNumber, formData.step2.equipmentType, formData.step3.thermostat, formData.step4.modelStickerPlatePhoto, formData.step4.filterSize, unitIdFromUrl, isLoadingExistingData, today]); // Run when key formData fields change

  // Fetch workers list (placeholder - update endpoint when available)
  useEffect(() => {
    async function fetchWorkers() {
      try {
        // TODO: Replace with actual endpoint when available
        // const response = await fetch(`${API_BASE_URL}/api/field/workers`);
        // const data = await response.json();
        // setWorkers(data.workers || []);
        
        // Worker names list
        setWorkers([
          { id: '1', name: 'Hemen' },
          { id: '2', name: 'Ali' },
          { id: '3', name: 'Alex' },
          { id: '4', name: 'Satyam' },
          { id: '5', name: 'Daniel' },
          { id: '6', name: 'Deepak' },
          { id: '7', name: 'Muin' },
          { id: '8', name: 'Nathaniel' },
          { id: '9', name: 'Ahmed' },
          { id: '10', name: 'Sabir' },
          { id: '11', name: 'Shivam' },
          { id: '12', name: 'Alireza' },
          { id: '13', name: 'Tarun' },
          { id: '14', name: 'Subin' },
          { id: '15', name: 'Resh' },
          { id: '16', name: 'Amrit' },
          { id: '17', name: 'Samantha' },
          { id: '18', name: 'Joseph' },
          { id: '19', name: 'Dynare' },
          { id: '20', name: 'Rathu' },
          { id: '21', name: 'Ali W' },
          { id: '22', name: 'Giseld' },
          { id: '23', name: 'Mehdi' },
          { id: '24', name: 'Rasoul' },
          { id: '25', name: 'Sean' },
          { id: '26', name: 'Luay' },
        ]);
      } catch (error) {
        console.error('[AddUnitForm] Failed to fetch workers:', error);
        setWorkers([]);
      } finally {
        setIsLoadingWorkers(false);
      }
    }
    fetchWorkers();
  }, []);

  // Helper function to deep compare values (handles arrays, objects, primitives)
  const deepEqual = (val1, val2) => {
    // Handle null/undefined
    if (val1 === null || val1 === undefined) return val2 === null || val2 === undefined;
    if (val2 === null || val2 === undefined) return false;
    
    // Handle arrays
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      // Sort arrays for comparison (for file URLs, order might differ but content is same)
      const sorted1 = [...val1].sort();
      const sorted2 = [...val2].sort();
      return sorted1.every((item, index) => deepEqual(item, sorted2[index]));
    }
    if (Array.isArray(val1) || Array.isArray(val2)) return false;
    
    // Handle objects
    if (typeof val1 === 'object' && typeof val2 === 'object') {
      // Get all unique keys from both objects
      const allKeys = new Set([...Object.keys(val1), ...Object.keys(val2)]);
      
      // Check that all keys have equal values (or both are missing/empty)
      for (const key of allKeys) {
        const v1 = val1[key];
        const v2 = val2[key];
        
        // If key is missing in one object, treat as empty string/array for comparison
        const normalizedV1 = v1 === undefined ? (Array.isArray(v2) ? [] : '') : v1;
        const normalizedV2 = v2 === undefined ? (Array.isArray(v1) ? [] : '') : v2;
        
        // Normalize empty strings and empty arrays to be equivalent
        const finalV1 = (normalizedV1 === '' || (Array.isArray(normalizedV1) && normalizedV1.length === 0)) 
          ? (Array.isArray(normalizedV2) ? [] : '') 
          : normalizedV1;
        const finalV2 = (normalizedV2 === '' || (Array.isArray(normalizedV2) && normalizedV2.length === 0)) 
          ? (Array.isArray(normalizedV1) ? [] : '') 
          : normalizedV2;
        
        if (!deepEqual(finalV1, finalV2)) {
          return false;
        }
      }
      return true;
    }
    
    // Handle primitives (including strings)
    // Treat empty string and empty array as equivalent for comparison purposes
    if (val1 === '' && (val2 === null || val2 === undefined || (Array.isArray(val2) && val2.length === 0))) return true;
    if (val2 === '' && (val1 === null || val1 === undefined || (Array.isArray(val1) && val1.length === 0))) return true;
    
    return val1 === val2;
  };

  const onSubmitStep1 = async (data) => {
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Submitting Step 1: General Information');
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Step 1 data:', data);
    console.log('[AddUnitForm] Current unitId:', unitId);
    console.log('[AddUnitForm] Building ID:', buildingId);
    console.log('[AddUnitForm] Maintenance ID:', maintenanceID);

    // ─── Uniform & Name Tag check-in gate ──────────────────────────────
    // First inspection of the day (no unitId yet) MUST be preceded by a
    // live uniform & name-tag check for this technician. We park the
    // submission and re-run it after the modal completes. The gate is
    // enabled fleet-wide via `requiresUniformCheck` above.
    if (requiresUniformCheck && !unitId) {
      const worker = (data.workerName || '').trim();
      const dateStr = serviceDayNow();
      const cacheKey = checkInLocalKey(worker, dateStr);
      // Backend is the source of truth. If the admin deletes a bad
      // selfie from the dashboard, the very next submit attempt must
      // re-prompt — even if the tech's localStorage still says "done".
      let backendSaysDone = null;
      if (worker) {
        try {
          const url = `${API_BASE_URL}/api/field/daily-check-in?technician=${encodeURIComponent(worker)}&date=${encodeURIComponent(dateStr)}`;
          const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
          if (res.ok) {
            const json = await res.json().catch(() => ({}));
            backendSaysDone = !!(json && json.exists);
          }
        } catch (err) {
          console.warn('[AddUnitForm] daily-check-in re-verify failed:', err);
        }
      }
      if (backendSaysDone === false) {
        // Authoritative: backend says no record. Force modal regardless
        // of cache state and wipe stale cache so the gate is consistent
        // across refreshes.
        try { localStorage.removeItem(cacheKey); } catch {}
        setCheckInDone(false);
        setPendingStep1Submit(data);
        setCheckInModalOpen(true);
        return;
      }
      if (backendSaysDone === true) {
        try {
          // Cache the confirmation so subsequent submits skip the
          // network round-trip.
          if (!localStorage.getItem(cacheKey)) {
            localStorage.setItem(cacheKey, JSON.stringify({ confirmedAt: Date.now() }));
          }
        } catch {}
        setCheckInDone(true);
        // Fall through to normal submit flow.
      } else if (!checkInDone) {
        // Network failed AND no prior cache — be safe and prompt.
        let cacheFallback = false;
        try { cacheFallback = !!localStorage.getItem(cacheKey); } catch {}
        if (!cacheFallback) {
          setPendingStep1Submit(data);
          setCheckInModalOpen(true);
          return;
        }
        setCheckInDone(true);
      }
    }
    
    // Check if editing existing unit and values haven't changed
    if (unitId && originalValues) {
      const currentValues = {
        unitNumber: data.unitNumber,
        maintenanceDate: data.maintenanceDate,
        workerName: data.workerName,
      };
      const originalStep1 = originalValues.step1;
      
      if (deepEqual(currentValues, originalStep1)) {
        console.log('[AddUnitForm] Step 1: No changes detected, skipping API call');
        // Store Step 1 data in state (for consistency)
        setFormData((prev) => ({
          ...prev,
          step1: {
            unitNumber: data.unitNumber,
            maintenanceDate: data.maintenanceDate,
            workerName: data.workerName,
          },
        }));
        // Move to next step without API call
        setCurrentStep(2);
        return;
      }
    }
    
    try {
      // Submit Step 1 to backend
      const step1Values = {
        unitNumber: data.unitNumber,
        maintenanceDate: data.maintenanceDate,
        workerName: data.workerName,
      };
      console.log('[AddUnitForm] Sending Step 1 values to backend:', step1Values);
      
      const response = await submitStep(
        1,
        unitId,
        buildingId,
        maintenanceID,
        step1Values
      );
      
      console.log('[AddUnitForm] Step 1 response from backend:', response);
      
      // Store unitId if this is a new submission
      if (response.unitId && !unitId) {
        console.log('[AddUnitForm] New unitId received:', response.unitId);
        setUnitId(response.unitId);
      } else if (response.unitId) {
        console.log('[AddUnitForm] Updated existing unitId:', response.unitId);
      }
      
      // Store Step 1 data in state
      setFormData((prev) => ({
        ...prev,
        step1: {
          unitNumber: data.unitNumber,
          maintenanceDate: data.maintenanceDate,
          workerName: data.workerName,
        },
      }));
      
      // Move to next step
      setCurrentStep(2);
    } catch (error) {
      console.error('[AddUnitForm] Error submitting Step 1:', error);
      alert(`Failed to save Step 1: ${error.message}`);
    }
  };

  const onSubmitStep2 = async (data) => {
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Submitting Step 2: Equipment');
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Step 2 data:', data);
    console.log('[AddUnitForm] Current unitId:', unitId);
    
    // Check if editing existing unit and values haven't changed
    if (unitId && originalValues) {
      const currentValues = {
        equipmentType: data.equipmentType,
        equipmentLocation: data.equipmentLocation,
        unitMissed: data.unitMissed,
        missedUnitPhoto: data.missedUnitPhoto || [],
        missedUnitReason: data.missedUnitReason || '',
        beforeFanCoilPhoto: data.beforeFanCoilPhoto || [],
      };
      const originalStep2 = originalValues.step2;
      
      if (deepEqual(currentValues, originalStep2)) {
        console.log('[AddUnitForm] Step 2: No changes detected, skipping API call');
        // Store Step 2 data in state (for consistency)
        setFormData((prev) => ({
          ...prev,
          step2: {
            equipmentType: data.equipmentType,
            equipmentLocation: data.equipmentLocation,
            unitMissed: data.unitMissed,
            missedUnitPhoto: data.missedUnitPhoto || [],
            missedUnitReason: data.missedUnitReason || '',
            beforeFanCoilPhoto: data.beforeFanCoilPhoto || [],
          },
        }));
        
        // Handle missed unit navigation
        if (data.unitMissed === 'Yes') {
          // Navigate back to building page for missed units
          const fullUrl = `/schedule/buildings/${buildingId}?maintenanceID=${maintenanceID}`;
          window.location.href = fullUrl;
          return;
        }
        
        // Move to next step without API call
        setCurrentStep(3);
        return;
      }
    }
    
    try {
      // Submit Step 2 to backend
      const step2Values = {
        equipmentType: data.equipmentType,
        equipmentLocation: data.equipmentLocation,
        unitMissed: data.unitMissed,
        missedUnitPhoto: data.missedUnitPhoto || [],
        missedUnitReason: data.missedUnitReason || '',
        beforeFanCoilPhoto: data.beforeFanCoilPhoto || [],
      };
      console.log('[AddUnitForm] Sending Step 2 values to backend:', step2Values);
      
      const response = await submitStep(
        2,
        unitId,
        buildingId,
        maintenanceID,
        step2Values
      );
      
      console.log('[AddUnitForm] Step 2 response from backend:', response);
      
      // Store unitId if this is a new submission
      if (response.unitId && !unitId) {
        console.log('[AddUnitForm] New unitId received:', response.unitId);
        setUnitId(response.unitId);
      } else if (response.unitId) {
        console.log('[AddUnitForm] Updated existing unitId:', response.unitId);
      }
      
      // Store Step 2 data in state
      setFormData((prev) => ({
        ...prev,
        step2: {
          equipmentType: data.equipmentType,
          equipmentLocation: data.equipmentLocation,
          unitMissed: data.unitMissed,
          missedUnitPhoto: data.missedUnitPhoto || [],
          missedUnitReason: data.missedUnitReason || '',
          beforeFanCoilPhoto: data.beforeFanCoilPhoto || [],
        },
      }));
      
      // Check if unit was missed - check both the form data and the stored value
      const isMissed = data.unitMissed === 'Yes' || data.unitMissed === 'yes';
      console.log('[AddUnitForm] Checking if unit was missed:', {
        unitMissed: data.unitMissed,
        isMissed: isMissed,
        type: typeof data.unitMissed
      });
      
      // If unit was missed, navigate back to building page immediately
      if (isMissed) {
        console.log('[AddUnitForm] Unit was missed, navigating back to building page...');
        const params = new URLSearchParams();
        if (maintenanceID) params.set('maintenanceID', maintenanceID);
        // Commented out refresh parameter for now
        // params.set('refresh', Date.now().toString());
        const queryString = params.toString();
        const fullUrl = `/schedule/buildings/${buildingId}${queryString ? `?${queryString}` : ''}`;
        console.log('[AddUnitForm] Navigating to:', fullUrl);
        // Use window.location.href for more reliable navigation
        setTimeout(() => {
          console.log('[AddUnitForm] About to navigate using window.location...');
          window.location.href = fullUrl;
        }, 100);
        return;
      }
      
      console.log('[AddUnitForm] Unit was not missed, moving to Step 3');
      // Move to next step
      setCurrentStep(3);
    } catch (error) {
      console.error('[AddUnitForm] Error submitting Step 2:', error);
      alert(`Failed to save Step 2: ${error.message}`);
    }
  };

  const onSubmitStep3 = async (data) => {
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Submitting Step 3: Components Check');
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Step 3 data:', data);
    console.log('[AddUnitForm] Current unitId:', unitId);
    
    // Check if editing existing unit and values haven't changed
    if (unitId && originalValues) {
      const normalizedCurrentValues = ensureStep3FieldDefaults(data);
      const originalStep3 = ensureStep3FieldDefaults(originalValues.step3 || {});
      
      if (deepEqual(normalizedCurrentValues, originalStep3)) {
        console.log('[AddUnitForm] Step 3: No changes detected, skipping API call');
        // Store Step 3 data in state (for consistency)
        setFormData((prev) => ({
          ...prev,
          step3: normalizedCurrentValues,
        }));
        // Move to next step without API call
        setCurrentStep(4);
        return;
      }
    }
    
    try {
      // Submit Step 3 to backend
      console.log('[AddUnitForm] Sending Step 3 values to backend:', data);
      
      const response = await submitStep(
        3,
        unitId,
        buildingId,
        maintenanceID,
        data
      );
      
      console.log('[AddUnitForm] Step 3 response from backend:', response);
      
      // Store unitId if this is a new submission
      if (response.unitId && !unitId) {
        console.log('[AddUnitForm] New unitId received:', response.unitId);
        setUnitId(response.unitId);
      } else if (response.unitId) {
        console.log('[AddUnitForm] Updated existing unitId:', response.unitId);
      }
      
      // Store Step 3 data in state
      setFormData((prev) => ({
        ...prev,
        step3: ensureStep3FieldDefaults(data),
      }));
      
      // Move to next step (Step 4)
      setCurrentStep(4);
    } catch (error) {
      console.error('[AddUnitForm] Error submitting Step 3:', error);
      alert(`Failed to save Step 3: ${error.message}`);
    }
  };

  const onSubmitStep4 = async (data) => {
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Submitting Step 4: Extra Info');
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Step 4 data:', data);
    console.log('[AddUnitForm] Current unitId:', unitId);
    
    // Check if editing existing unit and values haven't changed
    if (unitId && originalValues) {
      const currentValues = {
        modelStickerPlatePhoto: data.modelStickerPlatePhoto || [],
        additionalPhotos: data.additionalPhotos || [],
        residentEmail: data.residentEmail || '',
        additionalNotes: data.additionalNotes || '',
        filterSize: data.filterSize || '',
        filterSizeOther: data.filterSizeOther || '',
        newFilterPhoto: data.newFilterPhoto || [],
      };
      const originalStep4 = originalValues.step4;
      
      if (deepEqual(currentValues, originalStep4)) {
        console.log('[AddUnitForm] Step 4: No changes detected, skipping API call');
        // Store Step 4 data in state (for consistency)
        setFormData((prev) => ({
          ...prev,
          step4: data,
        }));
        // Move to next step without API call
        setCurrentStep(5);
        return;
      }
    }
    
    try {
      // Submit Step 4 to backend
      console.log('[AddUnitForm] Sending Step 4 values to backend:', data);
      
      const response = await submitStep(
        4,
        unitId,
        buildingId,
        maintenanceID,
        data
      );
      
      console.log('[AddUnitForm] Step 4 response from backend:', response);
      
      // Store unitId if this is a new submission
      if (response.unitId && !unitId) {
        console.log('[AddUnitForm] New unitId received:', response.unitId);
        setUnitId(response.unitId);
      } else if (response.unitId) {
        console.log('[AddUnitForm] Updated existing unitId:', response.unitId);
      }
      
      // Store Step 4 data in state
      setFormData((prev) => ({
        ...prev,
        step4: data,
      }));
      
      // Move to review step (Step 5)
      setCurrentStep(5);
    } catch (error) {
      console.error('[AddUnitForm] Error submitting Step 4:', error);
      alert(`Failed to save Step 4: ${error.message}`);
    }
  };

  const handleDeleteUnit = async () => {
    if (!unitId || isDeleting) return; // Can't delete if no unitId or already deleting
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/field/units/${unitId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to delete unit: ${response.status}`);
      }

      console.log('[AddUnitForm] Unit deleted successfully');
      
      // Navigate back to the building run page
      if (buildingId) {
        const params = new URLSearchParams();
        if (maintenanceID) params.set('maintenanceID', maintenanceID);
        // Commented out refresh parameter for now
        // params.set('refresh', Date.now().toString());
        const queryString = params.toString();
        const backUrl = `/schedule/buildings/${buildingId}${queryString ? `?${queryString}` : ''}`;
        navigate(backUrl, { replace: true });
      } else {
        navigate('/schedule');
      }
    } catch (error) {
      console.error('[AddUnitForm] Error deleting unit:', error);
      alert(`Failed to delete unit: ${error.message}`);
      setIsDeleting(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (isSubmittingFinal) return; // Prevent double submission
    
    setIsSubmittingFinal(true);
    
    // Get all collected form data
    const allFormData = getAllFormData();
    
    // Log all data to console
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Final Submit - Complete Form Data:');
    console.log('[AddUnitForm] ========================================');
    console.log(JSON.stringify(allFormData, null, 2));
    console.log('[AddUnitForm] ========================================');
    console.log('[AddUnitForm] Current unitId:', unitId);
    
    try {
      // Submit Step 5 (completion) to backend
      // This marks the submission as complete
      const step5Values = {
        completed: true,
        completedAt: new Date().toISOString(),
      };
      console.log('[AddUnitForm] Submitting Step 5 (completion) to backend:', step5Values);
      
      const response = await submitStep(
        5,
        unitId,
        buildingId,
        maintenanceID,
        step5Values
      );
      
      console.log('[AddUnitForm] ========================================');
      console.log('[AddUnitForm] Form completed successfully!');
      console.log('[AddUnitForm] Final response:', response);
      console.log('[AddUnitForm] ========================================');
      
      // Show success modal with Yellow Slip Id if there are issues
      const hasIssues = response.has_issues === true;
      const loginId = response.login_id;
      
      // Set modal data and show modal
      setSuccessModalData({ hasIssues, loginId });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('[AddUnitForm] Error completing form:', error);
      alert(`Failed to complete form: ${error.message}`);
      setIsSubmittingFinal(false); // Reset on error so user can try again
    }
  };
  
  // Watch all form values to check if required fields are filled for Step 1
  const unitNumber = step1Form.watch('unitNumber');
  const maintenanceDate = step1Form.watch('maintenanceDate');
  const workerName = step1Form.watch('workerName');
  
  // Check if all required fields for Step 1 are filled
  const isStep1Valid = 
    unitNumber?.trim() !== '' &&
    maintenanceDate?.trim() !== '' &&
    workerName?.trim() !== '';

  // Identifies this unit to the duplicate-photo check, and is what switches
  // that check on: FileUpload only runs it when a provider is present, so
  // photo fields elsewhere in the app are unaffected. Memoized because it's a
  // context value — a fresh object each render would re-render every upload
  // field on every keystroke.
  const dedupeContext = React.useMemo(() => ({
    buildingId: buildingId || '',
    buildingName: buildingName || '',
    unitId: unitId || '',
    unitNumber: (unitNumber || '').trim(),
    maintenanceID: maintenanceID || '',
    workerName: (workerName || '').trim(),
  }), [buildingId, buildingName, unitId, unitNumber, maintenanceID, workerName]);

  // Once we know the worker, check whether they've already done the
  // Uniform & Name Tag check for the CURRENT service day. We use
  // localStorage as an OPTIMISTIC initial state for snappy UX, but
  // always reconcile with the backend so admin actions (e.g. deleting a
  // bad selfie from the dashboard) actually force a re-prompt on the
  // tech's phone. Skip the lookup entirely when editing an existing
  // unit. We intentionally key on the service day of NOW (not
  // maintenanceDate) so a tech back-filling Tuesday's units on
  // Wednesday still gets prompted for Wednesday's uniform check.
  useEffect(() => {
    if (!requiresUniformCheck) {
      setCheckInDone(true);
      return;
    }
    if (unitIdFromUrl) return; // editing an existing unit, no gate
    const worker = (workerName || '').trim();
    if (!worker) {
      setCheckInDone(false);
      return;
    }
    const dateStr = serviceDayNow();
    const cacheKey = checkInLocalKey(worker, dateStr);

    let hasCache = false;
    try {
      hasCache = !!localStorage.getItem(cacheKey);
    } catch {}
    // Optimistic UI: if we have a local cache assume "done" while we
    // verify with the server. If we don't, default to "not done" and
    // wait for the lookup to either confirm or re-prompt.
    setCheckInDone(hasCache);

    let cancelled = false;
    setCheckInLookupInFlight(true);
    (async () => {
      try {
        const url = `${API_BASE_URL}/api/field/daily-check-in?technician=${encodeURIComponent(worker)}&date=${encodeURIComponent(dateStr)}`;
        const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
        if (cancelled) return;
        if (!res.ok) {
          // Network/server hiccup — fall back to whatever the cache
          // said so we don't accidentally block the tech mid-shift.
          return;
        }
        const json = await res.json().catch(() => ({}));
        if (json && json.exists) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify({
              photoUrl: json.photoUrl,
              createdAtEpoch: json.createdAtEpoch,
            }));
          } catch {}
          setCheckInDone(true);
        } else {
          // Backend says no record. Clear any stale cache so a delete
          // performed from the dashboard actually forces the re-prompt
          // here on the tech's phone.
          try { localStorage.removeItem(cacheKey); } catch {}
          setCheckInDone(false);
        }
      } catch (err) {
        console.warn('[AddUnitForm] daily-check-in lookup failed:', err);
      } finally {
        if (!cancelled) setCheckInLookupInFlight(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerName, unitIdFromUrl, requiresUniformCheck]);

  // Watch Step 2 form values
  const step2EquipmentType = step2Form.watch('equipmentType');
  const step2EquipmentLocation = step2Form.watch('equipmentLocation');
  const unitMissed = step2Form.watch('unitMissed');
  const missedUnitPhoto = step2Form.watch('missedUnitPhoto');
  const missedUnitReason = step2Form.watch('missedUnitReason');
  const beforeFanCoilPhoto = step2Form.watch('beforeFanCoilPhoto');
  
  // Check if all required fields for Step 2 are filled
  const isStep2Valid = 
    step2EquipmentType?.trim() !== '' &&
    step2EquipmentLocation?.trim() !== '' &&
    unitMissed?.trim() !== '' &&
    (
      unitMissed === 'No'
        ? (Array.isArray(beforeFanCoilPhoto) && beforeFanCoilPhoto.length > 0)
        : (Array.isArray(missedUnitPhoto) && missedUnitPhoto.length > 0 && missedUnitReason?.trim() !== '')
    );

  // Function to go back to a previous step
  const goToStep = (step) => {
    setCurrentStep(step);
    
    // Restore form values when going back
    if (step === 1) {
      step1Form.setValue('unitNumber', formData.step1.unitNumber);
      step1Form.setValue('maintenanceDate', formData.step1.maintenanceDate);
      step1Form.setValue('workerName', formData.step1.workerName);
    } else if (step === 2) {
      step2Form.setValue('equipmentType', formData.step2.equipmentType);
      step2Form.setValue('equipmentLocation', formData.step2.equipmentLocation);
      step2Form.setValue('unitMissed', formData.step2.unitMissed);
      step2Form.setValue('missedUnitPhoto', formData.step2.missedUnitPhoto);
      step2Form.setValue('missedUnitReason', formData.step2.missedUnitReason);
      step2Form.setValue('beforeFanCoilPhoto', formData.step2.beforeFanCoilPhoto || []);
    } else if (step === 3) {
      // Restore all Step 3 values
      Object.keys(formData.step3).forEach((key) => {
        step3Form.setValue(key, formData.step3[key] || '');
      });
    } else if (step === 4) {
      step4Form.setValue('modelStickerPlatePhoto', formData.step4.modelStickerPlatePhoto);
      step4Form.setValue('additionalPhotos', formData.step4.additionalPhotos);
      step4Form.setValue('residentEmail', formData.step4.residentEmail);
      step4Form.setValue('additionalNotes', formData.step4.additionalNotes);
      step4Form.setValue('filterSize', formData.step4.filterSize || '');
      step4Form.setValue('filterSizeOther', formData.step4.filterSizeOther || '');
      step4Form.setValue('newFilterPhoto', formData.step4.newFilterPhoto || []);
    }
  };
  
  // Access all collected form data (for debugging/submission)
  const getAllFormData = () => {
    return {
      ...formData,
      currentStep1Values: step1Form.getValues(),
      currentStep2Values: step2Form.getValues(),
      currentStep3Values: step3Form.getValues(),
      currentStep4Values: step4Form.getValues(),
    };
  };

  // Watch Step 4 form values for validation
  const modelStickerPlatePhoto = step4Form.watch('modelStickerPlatePhoto');
  const filterSize = step4Form.watch('filterSize');
  const filterSizeOther = step4Form.watch('filterSizeOther');
  const newFilterPhoto = step4Form.watch('newFilterPhoto');
  const isFilterInfoValid = !tracksFilterDetails || (
    Boolean(filterSize)
    && (filterSize !== 'Other' || Boolean(filterSizeOther?.trim()))
    && Array.isArray(newFilterPhoto) && newFilterPhoto.length > 0
  );
  const isStep4Valid = Boolean(modelStickerPlatePhoto && modelStickerPlatePhoto.length > 0) && isFilterInfoValid;

  // Equipment type options
  const equipmentTypeOptions = [
    { id: 'fan-coil', name: 'Fan Coil', value: 'Fan Coil' },
    { id: 'heat-pump', name: 'Heat Pump', value: 'Heat Pump' },
    { id: 'dryer-bathroom-vent', name: 'Dryer / Bathroom Vent', value: 'Dryer / Bathroom Vent' },
    { id: 'erv', name: 'ERV', value: 'ERV' },
  ];

  // Equipment location options
  const equipmentLocationOptions = [
    { id: 'living-room', name: 'Living Room', value: 'Living Room' },
    { id: 'bedroom', name: 'Bedroom', value: 'Bedroom' },
    { id: 'den', name: 'Den', value: 'Den' },
    { id: 'hallway', name: 'Hallway', value: 'Hallway' },
    { id: 'washroom', name: 'Washroom', value: 'Washroom' },
    { id: 'closet', name: 'Closet', value: 'Closet' },
    { id: 'common-area', name: 'Common Area', value: 'Common Area' },
  ];

  return (
    <PhotoDedupeContext.Provider value={dedupeContext}>
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
      <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        padding: '2rem 1rem',
      }}
    >
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            type="button"
            onClick={() => {
              if (buildingId) {
                const params = new URLSearchParams();
                if (maintenanceID) params.set('maintenanceID', maintenanceID);
                const queryString = params.toString();
                const backUrl = `/schedule/buildings/${buildingId}${queryString ? `?${queryString}` : ''}`;
                console.log('[AddUnitForm] Back button clicked, navigating to:', backUrl);
                // Use window.location.href for more reliable navigation
                window.location.href = backUrl;
              } else {
                console.log('[AddUnitForm] Back button clicked, navigating to /field');
                window.location.href = '/schedule';
              }
            }}
            style={{
              marginBottom: '1rem',
              background: 'none',
              border: 'none',
              color: '#2563eb',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            ← Back
          </button>
          <h1
            style={{
              fontSize: '1.75rem',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '0.5rem',
            }}
          >
            {currentStep === 1 && 'Step 1: General Information'}
            {currentStep === 2 && 'Step 2: Equipment'}
            {currentStep === 3 && 'Step 3: Components Check'}
            {currentStep === 4 && 'Step 4: Extra Info'}
            {currentStep === 5 && 'Review & Submit'}
          </h1>
          {buildingName && (
            <p
              style={{
                fontSize: '1rem',
                color: '#6b7280',
                marginBottom: '1.5rem',
              }}
            >
              {(() => {
                // For steps 2, 3, 4: show building name and unit number
                if (currentStep >= 2 && currentStep <= 4) {
                  const unitNumber = step1Form.watch('unitNumber') || formData.step1.unitNumber || '';
                  if (unitNumber) {
                    return `${buildingName}, Unit ${unitNumber}`;
                  }
                }
                // For step 1 and step 5: just show building name
                return buildingName;
              })()}
            </p>
          )}
        </div>

        {/* Progress indicator */}
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '2rem',
          }}
        >
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              style={{
                flex: 1,
                height: '4px',
                backgroundColor: step <= currentStep ? '#2563eb' : '#e5e7eb',
                borderRadius: '2px',
                transition: 'background-color 0.2s',
              }}
            />
          ))}
        </div>

        {/* Form Card */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.75rem',
            padding: '2rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {currentStep === 1 && (
            <form onSubmit={step1Form.handleSubmit(onSubmitStep1)}>
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '1.5rem',
                }}
              >
                Step 1: General Information
              </h2>

              {/* Unit Number */}
              <div style={{ marginBottom: '2.5rem' }}>
                <label
                  htmlFor="unitNumber"
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.75rem',
                  }}
                >
                  Unit Number <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  id="unitNumber"
                  type="text"
                  {...step1Form.register('unitNumber')}
                  placeholder="e.g., 101, 2A, 1505"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '1rem',
                    border: step1Form.formState.errors.unitNumber
                      ? '1px solid #ef4444'
                      : '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = step1Form.formState.errors.unitNumber
                      ? '#ef4444'
                      : '#d1d5db';
                  }}
                />
                {step1Form.formState.errors.unitNumber && (
                  <p
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#ef4444',
                    }}
                  >
                    {step1Form.formState.errors.unitNumber.message}
                  </p>
                )}
              </div>

              {/* Maintenance Date */}
              <div style={{ marginBottom: '2.5rem' }}>
                <label
                  htmlFor="maintenanceDate"
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.75rem',
                  }}
                >
                  Maintenance Date <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  id="maintenanceDate"
                  type="date"
                  {...step1Form.register('maintenanceDate')}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '1rem',
                    border: step1Form.formState.errors.maintenanceDate
                      ? '1px solid #ef4444'
                      : '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = step1Form.formState.errors.maintenanceDate
                      ? '#ef4444'
                      : '#d1d5db';
                  }}
                />
                {step1Form.formState.errors.maintenanceDate && (
                  <p
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#ef4444',
                    }}
                  >
                    {step1Form.formState.errors.maintenanceDate.message}
                  </p>
                )}
              </div>

              {/* Worker Name */}
              {isLoadingWorkers ? (
                <div
                  style={{
                    marginBottom: '2.5rem',
                    padding: '1rem',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '1rem',
                  }}
                >
                  Loading workers...
                </div>
              ) : (
                <SearchableSelect
                  label="Worker Name"
                  required
                  placeholder="Type or select a worker"
                  value={step1Form.watch('workerName')}
                  onChange={(value) => step1Form.setValue('workerName', value, { shouldValidate: true })}
                  options={workers}
                  hasError={!!step1Form.formState.errors.workerName}
                  errorMessage={step1Form.formState.errors.workerName?.message}
                />
              )}

              {/* Uniform & Name Tag check banner — only when about to gate */}
              {requiresUniformCheck && !unitId && workerName && !checkInDone && !checkInLookupInFlight && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    color: '#1F3A68',
                    background: '#eef3fb',
                    border: '1px solid #d6e0f3',
                    borderRadius: '0.5rem',
                    lineHeight: 1.4,
                  }}
                >
                  <strong>Uniform & Name Tag check</strong> — a quick live verification photo is required before your first unit today. You'll be prompted automatically when you tap Continue.
                </div>
              )}
              {requiresUniformCheck && !unitId && workerName && checkInDone && (
                <div
                  style={{
                    marginBottom: '1rem',
                    padding: '0.6rem 1rem',
                    fontSize: '0.875rem',
                    color: '#1c9550',
                    background: '#e3f5ea',
                    border: '1px solid #c6ead2',
                    borderRadius: '0.5rem',
                  }}
                >
                  ✓ Uniform & Name Tag check complete for today.
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={step1Form.formState.isSubmitting || !isStep1Valid}
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: isStep1Valid && !step1Form.formState.isSubmitting && !isAnyFileUploading ? '#ffffff' : '#9ca3af',
                  backgroundColor: isStep1Valid && !step1Form.formState.isSubmitting && !isAnyFileUploading ? '#2563eb' : '#e5e7eb',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: step1Form.formState.isSubmitting || !isStep1Valid || isAnyFileUploading ? 'not-allowed' : 'pointer',
                  opacity: step1Form.formState.isSubmitting || isAnyFileUploading ? 0.6 : 1,
                  transition: 'background-color 0.2s, color 0.2s, opacity 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!step1Form.formState.isSubmitting && isStep1Valid && !isAnyFileUploading) {
                    e.target.style.backgroundColor = '#1d4ed8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!step1Form.formState.isSubmitting && isStep1Valid && !isAnyFileUploading) {
                    e.target.style.backgroundColor = '#2563eb';
                  }
                }}
                disabled={step1Form.formState.isSubmitting || !isStep1Valid || isAnyFileUploading}
              >
                {step1Form.formState.isSubmitting ? 'Processing...' : isAnyFileUploading ? 'Uploading files...' : 'Continue'}
              </button>
            </form>
          )}

          {currentStep === 2 && (
            <form onSubmit={step2Form.handleSubmit(onSubmitStep2)}>
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '1.5rem',
                }}
              >
                Step 2: Equipment
              </h2>

              {/* Equipment Type */}
              <CustomSelect
                label="Select Equipment?"
                required
                placeholder="Select equipment type"
                value={step2Form.watch('equipmentType')}
                onChange={(value) => step2Form.setValue('equipmentType', value, { shouldValidate: true })}
                options={equipmentTypeOptions}
                hasError={!!step2Form.formState.errors.equipmentType}
                errorMessage={step2Form.formState.errors.equipmentType?.message}
              />

              {/* Equipment Location */}
              <CustomSelect
                label="Equipment Location"
                required
                placeholder="Select location"
                value={step2Form.watch('equipmentLocation')}
                onChange={(value) => step2Form.setValue('equipmentLocation', value, { shouldValidate: true })}
                options={equipmentLocationOptions}
                hasError={!!step2Form.formState.errors.equipmentLocation}
                errorMessage={step2Form.formState.errors.equipmentLocation?.message}
              />

              {/* Is this a missed unit? */}
              <CustomSelect
                label="Is this a missed unit?"
                required
                placeholder="Select an option"
                value={step2Form.watch('unitMissed')}
                onChange={(value) => {
                  step2Form.setValue('unitMissed', value, { shouldValidate: true });
                  
                  // If changing from "Yes" to "No", clear the missed unit fields
                  if (value === 'No') {
                    step2Form.setValue('missedUnitPhoto', [], { shouldValidate: true });
                    step2Form.setValue('missedUnitReason', '', { shouldValidate: true });
                    
                    // Update formData state to reflect the change
                    setFormData((prev) => ({
                      ...prev,
                      step2: {
                        ...prev.step2,
                        unitMissed: 'No',
                        missedUnitPhoto: [],
                        missedUnitReason: '',
                      },
                    }));
                  } else if (value === 'Yes') {
                    step2Form.setValue('beforeFanCoilPhoto', [], { shouldValidate: true });
                    setFormData((prev) => ({
                      ...prev,
                      step2: {
                        ...prev.step2,
                        unitMissed: 'Yes',
                        beforeFanCoilPhoto: [],
                      },
                    }));
                  }
                }}
                options={[
                  { id: 'no', name: 'No' },
                  { id: 'yes', name: 'Yes' },
                ]}
                hasError={!!step2Form.formState.errors.unitMissed}
                errorMessage={step2Form.formState.errors.unitMissed?.message}
              />

              {unitMissed === 'No' && (
                <div style={{ marginBottom: '2.5rem' }}>
                  <label
                    htmlFor="beforeFanCoilPhoto"
                    style={{
                      display: 'block',
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: '#374151',
                      marginBottom: '0.4rem',
                    }}
                  >
                    Before Picture of {step2EquipmentType === 'Heat Pump' ? 'Heat Pump' : step2EquipmentType === 'ERV' ? 'ERV' : step2EquipmentType === 'Dryer / Bathroom Vent' ? 'Dryer / Bathroom Vent' : 'Fan Coil'} <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <p
                    style={{
                      margin: '0 0 0.75rem',
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      lineHeight: 1.45,
                    }}
                  >
                    Take photo before maintenance showing the internal unit.
                  </p>
                  <FileUpload
                    label=""
                    required
                    value={step2Form.watch('beforeFanCoilPhoto') || []}
                    onChange={(urls) => {
                      step2Form.setValue('beforeFanCoilPhoto', urls, { shouldValidate: true });
                    }}
                    onUploadStatusChange={(isUploading) => {
                      setUploadStatuses(prev => ({ ...prev, beforeFanCoilPhoto: isUploading }));
                    }}
                    hasError={!!step2Form.formState.errors.beforeFanCoilPhoto}
                    errorMessage={step2Form.formState.errors.beforeFanCoilPhoto?.message}
                    multiple
                    dedupeField="Before photo"
                  />
                </div>
              )}

              {/* Conditional fields when unit is missed */}
              {unitMissed === 'Yes' && (
                <>
                  {/* Photo of Missed Unit */}
                  <div style={{ marginBottom: '2.5rem', marginLeft: '1.5rem' }}>
                    <label
                      htmlFor="missedUnitPhoto"
                      style={{
                        display: 'block',
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Photo of Missed Unit <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <FileUpload
                      label=""
                      required
                      value={step2Form.watch('missedUnitPhoto') || []}
                      onChange={(urls) => {
                        step2Form.setValue('missedUnitPhoto', urls, { shouldValidate: true });
                      }}
                      onUploadStatusChange={(isUploading) => {
                        setUploadStatuses(prev => ({ ...prev, missedUnitPhoto: isUploading }));
                      }}
                      hasError={!!step2Form.formState.errors.missedUnitPhoto}
                      errorMessage={step2Form.formState.errors.missedUnitPhoto?.message}
                      multiple
                      dedupeField="Missed unit"
                    />
                  </div>

                  {/* Reason for Missed Unit */}
                  <div style={{ marginBottom: '2.5rem', marginLeft: '1.5rem' }}>
                    <label
                      htmlFor="missedUnitReason"
                      style={{
                        display: 'block',
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Reason Missed? <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea
                      id="missedUnitReason"
                      {...step2Form.register('missedUnitReason')}
                      placeholder="Enter the reason the unit was missed..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        fontSize: '1rem',
                        border: step2Form.formState.errors.missedUnitReason
                          ? '1px solid #ef4444'
                          : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = step2Form.formState.errors.missedUnitReason
                          ? '#ef4444'
                          : '#2563eb';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = step2Form.formState.errors.missedUnitReason
                          ? '#ef4444'
                          : '#d1d5db';
                      }}
                    />
                    {step2Form.formState.errors.missedUnitReason && (
                      <p
                        style={{
                          marginTop: '0.5rem',
                          fontSize: '0.875rem',
                          color: '#ef4444',
                        }}
                      >
                        {step2Form.formState.errors.missedUnitReason.message || 'Reason is required when unit is missed'}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => goToStep(1)}
                  style={{
                    flex: 1,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#2563eb',
                    backgroundColor: '#ffffff',
                    border: '1px solid #2563eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={step2Form.formState.isSubmitting || !isStep2Valid}
                  style={{
                    flex: 2,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: isStep2Valid && !step2Form.formState.isSubmitting && !isAnyFileUploading ? '#ffffff' : '#9ca3af',
                    backgroundColor: isStep2Valid && !step2Form.formState.isSubmitting && !isAnyFileUploading ? '#2563eb' : '#e5e7eb',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: step2Form.formState.isSubmitting || !isStep2Valid || isAnyFileUploading ? 'not-allowed' : 'pointer',
                    opacity: step2Form.formState.isSubmitting || isAnyFileUploading ? 0.6 : 1,
                    transition: 'background-color 0.2s, color 0.2s, opacity 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!step2Form.formState.isSubmitting && isStep2Valid && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#1d4ed8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!step2Form.formState.isSubmitting && isStep2Valid && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#2563eb';
                    }
                  }}
                  disabled={step2Form.formState.isSubmitting || !isStep2Valid || isAnyFileUploading}
                >
                  {step2Form.formState.isSubmitting ? 'Processing...' : isAnyFileUploading ? 'Uploading files...' : 'Continue'}
                </button>
              </div>
            </form>
          )}

          {/* Step 3 - Component Checks */}
          {currentStep === 3 && normalizedEquipmentType && (normalizedEquipmentType === 'fan-coil' || normalizedEquipmentType === 'heat-pump') && (
            <form onSubmit={step3Form.handleSubmit(onSubmitStep3)}>
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '1.5rem',
                }}
              >
                Step 3: Components Check
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '0.95rem' }}>
                Please check each component and indicate if there are any issues.
              </p>

              {/* Component Check Helper Function */}
              {(() => {
                // Define components in order (compressor last for heat pump)
                const components = [
                  { key: 'thermostat', label: 'Thermostat' },
                  { key: 'leakSensor', label: 'Leak Sensor' },
                  { key: 'blowerFan', label: 'Blower Fan' },
                  { key: 'actuator', label: 'Actuator' },
                  { key: 'threeWayValve', label: '3-Way Valve' },
                  { key: 'drainPan', label: 'Drain Pan' },
                  { key: 'mould', label: 'Potential Mould' },
                  { key: 'valves', label: 'Valves' },
                  { key: 'hoses', label: 'Hoses' },
                  { key: 'coils', label: 'Coils' },
                  { key: 'insulation', label: 'Insulation' },
                ];

                // Add compressor only for heat pump, and make it last
                if (normalizedEquipmentType === 'heat-pump') {
                  components.push({ key: 'compressor', label: 'Compressor' });
                }

                return (
                  <>
                    {components.map((comp) => renderComponentCheck(comp.key, comp.label))}
                    {renderCustomIssuesBlock()}
                  </>
                );
              })()}

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => goToStep(2)}
                  style={{
                    flex: 1,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#2563eb',
                    backgroundColor: '#ffffff',
                    border: '1px solid #2563eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={step3Form.formState.isSubmitting}
                  style={{
                    flex: 2,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#ffffff',
                    backgroundColor: step3Form.formState.isSubmitting || isAnyFileUploading ? '#9ca3af' : '#2563eb',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: step3Form.formState.isSubmitting || isAnyFileUploading ? 'not-allowed' : 'pointer',
                    opacity: step3Form.formState.isSubmitting || isAnyFileUploading ? 0.6 : 1,
                    transition: 'background-color 0.2s, opacity 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!step3Form.formState.isSubmitting && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#1d4ed8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!step3Form.formState.isSubmitting && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#2563eb';
                    }
                  }}
                  disabled={step3Form.formState.isSubmitting || isAnyFileUploading}
                >
                  {step3Form.formState.isSubmitting ? 'Processing...' : isAnyFileUploading ? 'Uploading files...' : 'Continue'}
                </button>
              </div>
            </form>
          )}

          {/* Step 3 - Dryer / Bathroom Vent Components */}
          {currentStep === 3 && normalizedEquipmentType === 'dryer-bathroom-vent' && (
            <form onSubmit={step3Form.handleSubmit(onSubmitStep3)}>
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '1.5rem',
                }}
              >
                Step 3: Components Check
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '0.95rem' }}>
                Please complete the dryer / bathroom vent inspection checks below.
              </p>

              {(() => {
                const dryerComponents = [
                  { key: 'dryerTurningOn', label: 'Dryer Turning On?' },
                  { key: 'exhaustHoseCondition', label: 'Exhaust Hose Condition' },
                  { key: 'boosterFanCondition', label: 'Booster Fan Condition' },
                  { key: 'bathroomFanCondition', label: 'Bathroom Fan Condition' },
                ];

                return (
                  <>
                    {dryerComponents.map((comp) => renderComponentCheck(comp.key, comp.label))}

                    <div style={{ marginBottom: '2.5rem' }}>
                      <CustomSelect
                        label="Booster Fan Location"
                        required
                        placeholder="Select location"
                        value={step3Form.watch('boosterFanLocation')}
                        onChange={(value) => {
                          step3Form.setValue('boosterFanLocation', value, { shouldValidate: true });
                        }}
                        options={[
                          { id: 'Kitchen', name: 'Kitchen' },
                          { id: 'Living Room', name: 'Living Room' },
                          { id: 'Bathroom', name: 'Bathroom' },
                          { id: 'N/A', name: 'N/A' },
                        ]}
                        hasError={!!step3Form.formState.errors.boosterFanLocation}
                        errorMessage={step3Form.formState.errors.boosterFanLocation?.message}
                      />
                    </div>

                    {DRYER_PHOTO_ONLY_FIELDS.map((field) => (
                      <div key={field.key} style={{ marginBottom: '2.5rem' }}>
                        <label
                          htmlFor={field.key}
                          style={{
                            display: 'block',
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: '#374151',
                            marginBottom: '0.75rem',
                          }}
                        >
                          {field.label} <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <FileUpload
                          label=""
                          required
                          value={step3Form.watch(field.key) || []}
                          onChange={(urls) => {
                            step3Form.setValue(field.key, urls, { shouldValidate: true });
                          }}
                          onUploadStatusChange={(isUploading) => {
                            setUploadStatuses(prev => ({ ...prev, [field.key]: isUploading }));
                          }}
                          hasError={!!step3Form.formState.errors[field.key]}
                          errorMessage={step3Form.formState.errors[field.key]?.message}
                          multiple
                          dedupeField={field.label}
                        />
                      </div>
                    ))}

                    {renderCustomIssuesBlock()}
                  </>
                );
              })()}

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => goToStep(2)}
                  style={{
                    flex: 1,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#2563eb',
                    backgroundColor: '#ffffff',
                    border: '1px solid #2563eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={step3Form.formState.isSubmitting || isAnyFileUploading}
                  style={{
                    flex: 2,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#ffffff',
                    backgroundColor: step3Form.formState.isSubmitting || isAnyFileUploading ? '#9ca3af' : '#2563eb',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: step3Form.formState.isSubmitting || isAnyFileUploading ? 'not-allowed' : 'pointer',
                    opacity: step3Form.formState.isSubmitting || isAnyFileUploading ? 0.6 : 1,
                    transition: 'background-color 0.2s, opacity 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!step3Form.formState.isSubmitting && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#1d4ed8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!step3Form.formState.isSubmitting && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#2563eb';
                    }
                  }}
                >
                  {step3Form.formState.isSubmitting ? 'Processing...' : isAnyFileUploading ? 'Uploading files...' : 'Continue'}
                </button>
              </div>
            </form>
          )}

          {/* Step 3 - ERV Components */}
          {currentStep === 3 && normalizedEquipmentType === 'erv' && (
            <form onSubmit={step3Form.handleSubmit(onSubmitStep3)}>
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '1.5rem',
                }}
              >
                Step 3: ERV Inspection
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '0.95rem' }}>
                Please complete the ERV checks below.
              </p>

              {/* Before Photo (required) */}
              <div style={{ marginBottom: '2.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.75rem',
                  }}
                >
                  Before Photo <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <FileUpload
                  label=""
                  required
                  value={step3Form.watch('ervBeforePhoto') || []}
                  onChange={(urls) => {
                    step3Form.setValue('ervBeforePhoto', urls, { shouldValidate: true });
                  }}
                  onUploadStatusChange={(isUploading) => {
                    setUploadStatuses(prev => ({ ...prev, ervBeforePhoto: isUploading }));
                  }}
                  dedupeField="ERV before photo"
                />
                {step3Form.formState.errors.ervBeforePhoto && (
                  <div style={{ color: '#ef4444', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                    {step3Form.formState.errors.ervBeforePhoto.message}
                  </div>
                )}
              </div>

              {/* ERV Type */}
              <div style={{ marginBottom: '2.5rem' }}>
                <CustomSelect
                  label="What kind of ERV?"
                  required
                  placeholder="Select type"
                  value={step3Form.watch('ervType')}
                  onChange={(value) => {
                    step3Form.setValue('ervType', value, { shouldValidate: true });
                  }}
                  options={[
                    { id: 'combo-unit', name: 'Combo Unit', value: 'combo-unit' },
                    { id: 'single-unit', name: 'Single Unit', value: 'single-unit' },
                  ]}
                  hasError={!!step3Form.formState.errors.ervType}
                  errorMessage={step3Form.formState.errors.ervType?.message}
                />
              </div>

              {/* ERV Panel Condition */}
              {renderComponentCheck('ervPanelCondition', 'ERV Panel Condition')}

              {/* Any Other Custom Issues */}
              {renderCustomIssuesBlock()}

              {/* After Photo (required) */}
              <div style={{ marginBottom: '2.5rem', marginTop: '1rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.75rem',
                  }}
                >
                  After Photo <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <FileUpload
                  label=""
                  required
                  value={step3Form.watch('ervAfterPhoto') || []}
                  onChange={(urls) => {
                    step3Form.setValue('ervAfterPhoto', urls, { shouldValidate: true });
                  }}
                  onUploadStatusChange={(isUploading) => {
                    setUploadStatuses(prev => ({ ...prev, ervAfterPhoto: isUploading }));
                  }}
                  dedupeField="ERV after photo"
                />
                {step3Form.formState.errors.ervAfterPhoto && (
                  <div style={{ color: '#ef4444', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                    {step3Form.formState.errors.ervAfterPhoto.message}
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => goToStep(2)}
                  style={{
                    flex: 1,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#2563eb',
                    backgroundColor: '#ffffff',
                    border: '1px solid #2563eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={step3Form.formState.isSubmitting}
                  style={{
                    flex: 2,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#ffffff',
                    backgroundColor: step3Form.formState.isSubmitting || isAnyFileUploading ? '#9ca3af' : '#2563eb',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: step3Form.formState.isSubmitting || isAnyFileUploading ? 'not-allowed' : 'pointer',
                    opacity: step3Form.formState.isSubmitting || isAnyFileUploading ? 0.6 : 1,
                    transition: 'background-color 0.2s, opacity 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!step3Form.formState.isSubmitting && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#1d4ed8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!step3Form.formState.isSubmitting && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#2563eb';
                    }
                  }}
                  disabled={step3Form.formState.isSubmitting || isAnyFileUploading}
                >
                  {step3Form.formState.isSubmitting ? 'Processing...' : isAnyFileUploading ? 'Uploading files...' : 'Continue'}
                </button>
              </div>
            </form>
          )}

          {/* Step 4 - Final Details */}
          {currentStep === 4 && (
            <form onSubmit={step4Form.handleSubmit(onSubmitStep4)}>
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '1.5rem',
                }}
              >
                Step 4: Extra Info
              </h2>

              {/* Filter details - only equipment that takes a filter */}
              {tracksFilterDetails && (
                <>
                  <CustomSelect
                    label="Filter Size"
                    required
                    placeholder="Select filter size"
                    value={filterSize}
                    onChange={(value) => {
                      step4Form.setValue('filterSize', value, { shouldValidate: true });
                      if (value !== 'Other') {
                        step4Form.setValue('filterSizeOther', '', { shouldValidate: true });
                      }
                    }}
                    options={FILTER_SIZE_OPTIONS}
                    hasError={!!step4Form.formState.errors.filterSize}
                    errorMessage={step4Form.formState.errors.filterSize?.message}
                  />

                  {filterSize === 'Other' && (
                    <div style={{ marginLeft: '1.5rem', paddingLeft: '1.5rem', borderLeft: '2px solid #e5e7eb' }}>
                      <div style={{ marginBottom: '2.5rem' }}>
                        <label
                          htmlFor="filterSizeOther"
                          style={{
                            display: 'block',
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: '#374151',
                            marginBottom: '0.75rem',
                          }}
                        >
                          Describe the Filter Size <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <textarea
                          id="filterSizeOther"
                          {...step4Form.register('filterSizeOther')}
                          placeholder="Enter the exact filter size (ex. 12 x 18, or a custom/odd size)"
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '1rem',
                            fontSize: '1rem',
                            border: step4Form.formState.errors.filterSizeOther
                              ? '1px solid #ef4444'
                              : '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#2563eb';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = step4Form.formState.errors.filterSizeOther
                              ? '#ef4444'
                              : '#d1d5db';
                          }}
                        />
                        {step4Form.formState.errors.filterSizeOther && (
                          <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#ef4444' }}>
                            {step4Form.formState.errors.filterSizeOther.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ marginBottom: '2.5rem' }}>
                    <label
                      htmlFor="newFilterPhoto"
                      style={{
                        display: 'block',
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '0.75rem',
                      }}
                    >
                      Photo of New Filter inside the {filterEquipmentLabel} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <FileUpload
                      label=""
                      required
                      value={step4Form.watch('newFilterPhoto') || []}
                      onChange={(urls) => {
                        step4Form.setValue('newFilterPhoto', urls, { shouldValidate: true });
                      }}
                      onUploadStatusChange={(isUploading) => {
                        setUploadStatuses(prev => ({ ...prev, newFilterPhoto: isUploading }));
                      }}
                      hasError={!!step4Form.formState.errors.newFilterPhoto}
                      errorMessage={step4Form.formState.errors.newFilterPhoto?.message}
                      multiple
                      dedupeField="New filter"
                    />
                  </div>
                </>
              )}

              {/* Photo of Model Sticker Plate - Required */}
              <div style={{ marginBottom: '2.5rem' }}>
                <label
                  htmlFor="modelStickerPlatePhoto"
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.75rem',
                  }}
                >
                  Photo of Model Sticker Plate (all model stickers you see) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <FileUpload
                  label=""
                  required
                  value={step4Form.watch('modelStickerPlatePhoto') || []}
                  onChange={(urls) => {
                    step4Form.setValue('modelStickerPlatePhoto', urls, { shouldValidate: true });
                  }}
                  onUploadStatusChange={(isUploading) => {
                    setUploadStatuses(prev => ({ ...prev, modelStickerPlatePhoto: isUploading }));
                  }}
                  hasError={!!step4Form.formState.errors.modelStickerPlatePhoto}
                  errorMessage={step4Form.formState.errors.modelStickerPlatePhoto?.message}
                  multiple
                  dedupeField="Model sticker"
                />
              </div>

              {/* Additional Photos of the Unit - Optional */}
              <div style={{ marginBottom: '2.5rem' }}>
                <label
                  htmlFor="additionalPhotos"
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.75rem',
                  }}
                >
                  Additional Photos of the Unit
                </label>
                <FileUpload
                  label=""
                  value={step4Form.watch('additionalPhotos') || []}
                  onChange={(urls) => {
                    step4Form.setValue('additionalPhotos', urls, { shouldValidate: true });
                  }}
                  onUploadStatusChange={(isUploading) => {
                    setUploadStatuses(prev => ({ ...prev, additionalPhotos: isUploading }));
                  }}
                  hasError={!!step4Form.formState.errors.additionalPhotos}
                  errorMessage={step4Form.formState.errors.additionalPhotos?.message}
                  multiple
                  dedupeField="Additional photo"
                />
              </div>

              {/* Resident Email - Optional */}
              <div style={{ marginBottom: '2.5rem' }}>
                <label
                  htmlFor="residentEmail"
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.75rem',
                  }}
                >
                  Resident Email
                  <span
                    style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      fontWeight: 400,
                      marginTop: '0.5rem',
                    }}
                  >
                    Collect if there are any issues and the resident is there
                  </span>
                </label>
                <input
                  id="residentEmail"
                  type="email"
                  {...step4Form.register('residentEmail')}
                  placeholder="resident@example.com"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '1rem',
                    border: step4Form.formState.errors.residentEmail
                      ? '1px solid #ef4444'
                      : '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = step4Form.formState.errors.residentEmail
                      ? '#ef4444'
                      : '#d1d5db';
                  }}
                />
                {step4Form.formState.errors.residentEmail && (
                  <p
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#ef4444',
                    }}
                  >
                    {step4Form.formState.errors.residentEmail.message}
                  </p>
                )}
              </div>

              {/* Any Additional Notes - Optional */}
              <div style={{ marginBottom: '2.5rem' }}>
                <label
                  htmlFor="additionalNotes"
                  style={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.75rem',
                  }}
                >
                  Any Additional Notes?
                </label>
                <textarea
                  id="additionalNotes"
                  {...step4Form.register('additionalNotes')}
                  placeholder="Enter any additional notes or comments..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                  }}
                />
              </div>

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => goToStep(3)}
                  style={{
                    flex: 1,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#2563eb',
                    backgroundColor: '#ffffff',
                    border: '1px solid #2563eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={step4Form.formState.isSubmitting || !isStep4Valid}
                  style={{
                    flex: 2,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: isStep4Valid && !step4Form.formState.isSubmitting && !isAnyFileUploading ? '#ffffff' : '#9ca3af',
                    backgroundColor: isStep4Valid && !step4Form.formState.isSubmitting && !isAnyFileUploading ? '#2563eb' : '#e5e7eb',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: step4Form.formState.isSubmitting || !isStep4Valid || isAnyFileUploading ? 'not-allowed' : 'pointer',
                    opacity: step4Form.formState.isSubmitting || isAnyFileUploading ? 0.6 : 1,
                    transition: 'background-color 0.2s, color 0.2s, opacity 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!step4Form.formState.isSubmitting && isStep4Valid && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#1d4ed8';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!step4Form.formState.isSubmitting && isStep4Valid && !isAnyFileUploading) {
                      e.target.style.backgroundColor = '#2563eb';
                    }
                  }}
                  disabled={step4Form.formState.isSubmitting || !isStep4Valid || isAnyFileUploading}
                >
                  {step4Form.formState.isSubmitting ? 'Processing...' : isAnyFileUploading ? 'Uploading files...' : 'Continue'}
                </button>
              </div>
            </form>
          )}

          {/* Placeholder for other equipment types or when equipment type is not set */}
          {currentStep === 3 && (!normalizedEquipmentType || (normalizedEquipmentType !== 'fan-coil' && normalizedEquipmentType !== 'heat-pump' && normalizedEquipmentType !== 'dryer-bathroom-vent' && normalizedEquipmentType !== 'erv')) && (
            <div>
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '1rem',
                }}
              >
                Component Checks - Coming Soon
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                Component checks for {equipmentType || 'this equipment type'} will be implemented next.
              </p>
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#f9fafb', 
                borderRadius: '0.5rem', 
                fontSize: '0.875rem',
                marginBottom: '1rem'
              }}>
                <strong>Debug Info:</strong>
                <pre style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify({ equipmentType, formDataStep2: formData.step2 }, null, 2)}
                </pre>
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => goToStep(2)}
                  style={{
                    flex: 1,
                    padding: '1rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#2563eb',
                    backgroundColor: '#ffffff',
                    border: '1px solid #2563eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#eff6ff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  ← Back to Step 2
                </button>
              </div>
            </div>
          )}

          {/* Step 5 - Review & Submit */}
          {currentStep === 5 && (() => {
            const allData = getAllFormData();
            const step1Data = allData.currentStep1Values || {};
            const step2Data = allData.currentStep2Values || {};
            const step3Data = allData.currentStep3Values || {};
            const step4Data = allData.currentStep4Values || {};
            
            // Helper to format array values
            const formatArray = (arr) => {
              if (!arr || !Array.isArray(arr) || arr.length === 0) return 'None';
              return arr.join(', ');
            };
            
            // Helper to format file URLs
            const formatFiles = (arr) => {
              if (!arr || !Array.isArray(arr) || arr.length === 0) return 'None';
              return `${arr.length} file(s) uploaded`;
            };
            
            // Component labels mapping
            const componentLabels = {
              thermostat: 'Thermostat',
              leakSensor: 'Leak Sensor',
              blowerFan: 'Blower Fan',
              actuator: 'Actuator',
              threeWayValve: '3-Way Valve',
              drainPan: 'Drain Pan',
              mould: 'Potential Mould',
              valves: 'Valves',
              hoses: 'Hoses',
              coils: 'Coils',
              insulation: 'Insulation',
              compressor: 'Compressor',
              dryerTurningOn: 'Dryer Turning On?',
              exhaustHoseCondition: 'Exhaust Hose Condition',
              boosterFanCondition: 'Booster Fan Condition',
              bathroomFanCondition: 'Bathroom Fan Condition',
              ervPanelCondition: 'ERV Panel Condition',
            };
            
            return (
              <div>
                {/* Step 1 Data */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                    General Information
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#374151' }}>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Field</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Unit Number</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step1Data.unitNumber || '—'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Maintenance Date</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step1Data.maintenanceDate || '—'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Worker Name</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step1Data.workerName || '—'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Select Equipment</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step2Data.equipmentType || '—'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Equipment Location</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step2Data.equipmentLocation || '—'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Is this a missed unit?</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step2Data.unitMissed || '—'}</td>
                      </tr>
                      {step2Data.unitMissed === 'Yes' && (
                        <>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff', paddingLeft: '2.5rem' }}>Photo of Missed Unit</td>
                            <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step2Data.missedUnitPhoto)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff', paddingLeft: '2.5rem' }}>Reason Missed</td>
                            <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step2Data.missedUnitReason || '—'}</td>
                          </tr>
                        </>
                      )}
                      {step2Data.unitMissed !== 'Yes' && (
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Before Picture of Fan Coil</td>
                          <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step2Data.beforeFanCoilPhoto)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Step 3 Data - Component Checks */}
                {step3Data && Object.keys(step3Data).length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                      Components Check
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#374151' }}>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Component</th>
                          <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(componentLabels).map(([key, label], index) => {
                          const status = step3Data[key];
                          if (!status) return null;
                          
                          return (
                            <React.Fragment key={key}>
                              <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>{label}</td>
                                <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{status}</td>
                              </tr>
                              {status === 'HAS ISSUES 🔴' && (
                                <>
                                  <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', paddingLeft: '2.5rem' }}>Photo</td>
                                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data[`${key}Photo`])}</td>
                                  </tr>
                                  <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', paddingLeft: '2.5rem' }}>Description</td>
                                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data[`${key}Description`] || '—'}</td>
                                  </tr>
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                        
                        {/* Any Other Custom Issues */}
                        {step3Data.anyOtherCustomIssues && (
                          <>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>Any Other Custom Issues</td>
                              <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data.anyOtherCustomIssues}</td>
                            </tr>
                            {step3Data.anyOtherCustomIssues === 'HAS ISSUES 🔴' && (
                              <>
                                <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', paddingLeft: '2.5rem' }}>Photo</td>
                                  <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data.anyOtherCustomIssuesPhoto)}</td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', paddingLeft: '2.5rem' }}>Description</td>
                                  <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data.anyOtherCustomIssuesDescription || '—'}</td>
                                </tr>
                              </>
                            )}
                          </>
                        )}

                        {normalizedEquipmentType === 'dryer-bathroom-vent' && (
                          <>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>Booster Fan Location</td>
                              <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data.boosterFanLocation || '—'}</td>
                            </tr>
                            {DRYER_PHOTO_ONLY_FIELDS.map((field, index) => (
                              <tr
                                key={field.key}
                                style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}
                              >
                                <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>{field.label}</td>
                                <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data[field.key])}</td>
                              </tr>
                            ))}
                          </>
                        )}

                        {normalizedEquipmentType === 'erv' && (
                          <>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>ERV Type</td>
                              <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{step3Data.ervType || '—'}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>Before Photo</td>
                              <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data.ervBeforePhoto)}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151' }}>After Photo</td>
                              <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatFiles(step3Data.ervAfterPhoto)}</td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Step 4 Data */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                    Extra Info
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db', borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#374151' }}>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Field</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', borderBottom: '1px solid #4b5563' }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tracksFilterDetails && (
                        <>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Filter Size</td>
                            <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step4Data.filterSize || '—'}</td>
                          </tr>
                          {step4Data.filterSize === 'Other' && (
                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff', paddingLeft: '2.5rem' }}>Filter Size Details</td>
                              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step4Data.filterSizeOther || '—'}</td>
                            </tr>
                          )}
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Photo of New Filter</td>
                            <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step4Data.newFilterPhoto)}</td>
                          </tr>
                        </>
                      )}
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Photo of Model Sticker Plate</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step4Data.modelStickerPlatePhoto)}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Additional Photos</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{formatFiles(step4Data.additionalPhotos)}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Resident Email</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step4Data.residentEmail || '—'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#374151', backgroundColor: '#ffffff' }}>Additional Notes</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#6b7280', backgroundColor: '#ffffff' }}>{step4Data.additionalNotes || '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Navigation Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '2rem' }}>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(4)}
                      style={{
                        flex: 1,
                        padding: '1rem 1.5rem',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#2563eb',
                        backgroundColor: '#ffffff',
                        border: '1px solid #2563eb',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#eff6ff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#ffffff';
                      }}
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={handleFinalSubmit}
                      disabled={isSubmittingFinal}
                      style={{
                        flex: 2,
                        padding: '1rem 1.5rem',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#ffffff',
                        backgroundColor: isSubmittingFinal ? '#9ca3af' : '#2563eb',
                        border: 'none',
                        borderRadius: '0.5rem',
                        cursor: isSubmittingFinal ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        opacity: isSubmittingFinal ? 0.7 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubmittingFinal) {
                          e.target.style.backgroundColor = '#1d4ed8';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmittingFinal) {
                          e.target.style.backgroundColor = '#2563eb';
                        }
                      }}
                    >
                      {isSubmittingFinal ? (
                        <>
                          <svg
                            style={{
                              width: '1rem',
                              height: '1rem',
                              animation: 'spin 1s linear infinite',
                            }}
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              style={{ opacity: 0.25 }}
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              style={{ opacity: 0.75 }}
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                  
                  {/* Delete Unit Button - only show if unitId exists (editing existing unit) */}
                  {unitId && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: 400,
                        color: '#6b7280',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        opacity: isDeleting ? 0.6 : 1,
                        textDecoration: 'underline',
                      }}
                      onMouseEnter={(e) => {
                        if (!isDeleting) {
                          e.target.style.color = '#374151';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isDeleting) {
                          e.target.style.color = '#6b7280';
                        }
                      }}
                    >
                      Delete Unit
                    </button>
                  )}
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000,
                    }}
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '0.75rem',
                        padding: '1.5rem',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h3
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          color: '#111827',
                          marginBottom: '1rem',
                        }}
                      >
                        Delete Unit
                      </h3>
                      <p
                        style={{
                          fontSize: '1rem',
                          color: '#6b7280',
                          marginBottom: '1.5rem',
                        }}
                      >
                        Are you sure you want to delete this unit? This action cannot be undone.
                      </p>
                      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(false)}
                          style={{
                            padding: '0.75rem 1.5rem',
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: '#374151',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#e5e7eb';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#f3f4f6';
                          }}
                        >
                          No, go back
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteUnit}
                          disabled={isDeleting}
                          style={{
                            padding: '0.75rem 1.5rem',
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: '#ffffff',
                            backgroundColor: isDeleting ? '#9ca3af' : '#dc2626',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            opacity: isDeleting ? 0.7 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!isDeleting) {
                              e.target.style.backgroundColor = '#b91c1c';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isDeleting) {
                              e.target.style.backgroundColor = '#dc2626';
                            }
                          }}
                        >
                          {isDeleting ? 'Deleting...' : "Yes, I'm sure"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
      
      {/* Success Modal */}
      {showSuccessModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
            // Prevent scrolling on mobile
            overflow: 'hidden',
            // Ensure it works on all devices
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              // Prevent text selection issues on mobile
              WebkitUserSelect: 'none',
              userSelect: 'none',
              // Ensure proper rendering on all devices
              WebkitTransform: 'translateZ(0)',
              transform: 'translateZ(0)',
            }}
          >
            {/* Success Message */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2
                style={{
                  fontSize: '1.75rem',
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: '1rem',
                }}
              >
                Unit Added Successfully!
              </h2>
              
              {successModalData.hasIssues && successModalData.loginId && (
                <div style={{ marginTop: '2rem' }}>
                  <p
                    style={{
                      fontSize: '1rem',
                      color: '#6b7280',
                      marginBottom: '1rem',
                      fontWeight: 500,
                    }}
                  >
                    Yellow Slip Id:
                  </p>
                  <div
                    style={{
                      fontSize: '3rem',
                      fontWeight: 700,
                      color: '#2563eb',
                      letterSpacing: '0.1em',
                      marginBottom: '1.5rem',
                      fontFamily: 'monospace',
                      // Ensure large text renders properly on all devices
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                    }}
                  >
                    {successModalData.loginId}
                  </div>
                  <p
                    style={{
                      fontSize: '1.1rem',
                      color: '#374151',
                      fontWeight: 500,
                      lineHeight: '1.6',
                      marginTop: '1rem',
                    }}
                  >
                    Write this on the yellow paper and give it to the resident or leave it somewhere visible
                  </p>
                </div>
              )}
            </div>
            
            {/* OK Button */}
            <button
              type="button"
              onClick={() => {
                setShowSuccessModal(false);
                // Navigate back to the building run page
                if (buildingId) {
                  const params = new URLSearchParams();
                  if (maintenanceID) params.set('maintenanceID', maintenanceID);
                  const queryString = params.toString();
                  const backUrl = `/schedule/buildings/${buildingId}${queryString ? `?${queryString}` : ''}`;
                  navigate(backUrl, { replace: true });
                } else {
                  navigate('/schedule', { replace: true });
                }
              }}
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#ffffff',
                backgroundColor: '#2563eb',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                // Ensure button works on all devices
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1d4ed8';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#2563eb';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <UniformCheckInModal
        open={checkInModalOpen}
        technician={(workerName || '').trim()}
        date={serviceDayNow()}
        buildingId={buildingId || ''}
        buildingName={buildingName || ''}
        onCancel={() => {
          setCheckInModalOpen(false);
          setPendingStep1Submit(null);
        }}
        onComplete={() => {
          setCheckInModalOpen(false);
          setCheckInDone(true);
          const parked = pendingStep1Submit;
          setPendingStep1Submit(null);
          if (parked) {
            // Re-run the original Step 1 submit now that the gate has lifted
            setTimeout(() => { onSubmitStep1(parked); }, 0);
          }
        }}
      />
    </div>
    </PhotoDedupeContext.Provider>
  );
}

